/**
 * Privilege mapping management interface for configuring privilege-level classifications and role certifications.
 *
 * Each database technology has different raw privilege names; this component lets DBAs map them
 * to canonical tiers (NONE..SUPER_ADMIN). The set of tiers offered in the level dropdown is
 * scoped per-technology by the Privilege Matrix admin page.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSafeTimeout } from '../hooks';

import API_CONFIG from '../config/api.config';
import { useAuth } from '../context/AuthContext';
import { WideModal, CertificationWorkspace } from './common';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || API_CONFIG.getEndpoint('/api/databases');

/**
 * Renders the privilege mapping editor with bulk actions and role certification views.
 *
 * @param {Object} props
 * @param {string} props.technology - The database technology to manage mappings for (e.g., 'oracle', 'mssql').
 */
const ManagePrivilegeMappings = ({ technology }) => {
  // State management
  const [databaseTypes, setDatabaseTypes] = useState([]);
  const [selectedDatabaseType, setSelectedDatabaseType] = useState('');
  const [viewMode, setViewMode] = useState('privileges'); // 'privileges' or 'roles'
  const [supportsRoles, setSupportsRoles] = useState(false);
  const [privileges, setPrivileges] = useState([]);
  const [roles, setRoles] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [roleStatistics, setRoleStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  // Filters and sorting
  const [filters, setFilters] = useState({
    privilege_type: '',
    privilege_level_name: '',
    suggested_level_name: '',
    certified: '',
    search: '',
    certification_status: '' // For roles: '' | 'certified' | 'pending' | 'auto'
  });

  // Role type filters
  const [roleTypeFilters, setRoleTypeFilters] = useState({
    oracle_maintained: true,
    user_defined: true
  });

  // As-on-date filter for roles (defaults to today)
  const [asOnDate, setAsOnDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // Format: YYYY-MM-DD
  });

  const [sorting, setSorting] = useState({
    sort_by: 'privilege_level',
    sort_order: 'desc'
  });

  // Bulk update state
  const [bulkCertifyAction, setBulkCertifyAction] = useState('');
  const [bulkLevelChange, setBulkLevelChange] = useState('');
  // The actual signed-in user (application_users.username) — used as
  // certified_by / changed_by so the audit trail and certified-by display
  // show who really acted, not a generic placeholder. Falls back only if
  // the auth context hasn't resolved a user yet.
  const { user: authUser } = useAuth();
  const currentUser = authUser?.username || authUser?.email || 'unknown_user';

  // Edit privilege level modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPrivilege, setEditingPrivilege] = useState(null);
  const [editForm, setEditForm] = useState({
    privilege_level: 0,
    privilege_level_name: '',
    notes: ''
  });

  // Privilege detail modal state (for role privilege popups)
  const [showPrivilegeDetailModal, setShowPrivilegeDetailModal] = useState(false);
  const [privilegeDetailData, setPrivilegeDetailData] = useState({
    title: '',
    type: '',
    items: [],
    roleName: '',
    parentRoleName: '',
    parentDatabaseName: '',
    parentDbid: ''
  });

  // Role drill-down modal state
  const [showRoleDetailModal, setShowRoleDetailModal] = useState(false);
  const [roleDetailData, setRoleDetailData] = useState(null);

  // Role certification state
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [showEditRoleLevelModal, setShowEditRoleLevelModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [editRoleForm, setEditRoleForm] = useState({
    privilege_level: '',
    privilege_level_name: '',
    notes: '',
    certify_after_update: false
  });
  const [roleDetailLoading, setRoleDetailLoading] = useState(false);

  // Privilege levels - will be loaded from API
  const [privilegeLevels, setPrivilegeLevels] = useState([]);

  // Role recategorization state
  const [recategorizationStatus, setRecategorizationStatus] = useState(null);
  const [recategorizationLoading, setRecategorizationLoading] = useState(false);
  const [recategorizationMessage, setRecategorizationMessage] = useState('');
  // Confirmation modal for recategorization (replaces window.confirm). When any
  // privilege is UNASSIGNED or uncertified, role categorization would be
  // inaccurate (Oracle role level = MAX of component privilege levels, and
  // uncertified/unassigned privileges don't contribute a trustworthy level),
  // so the modal blocks the run and tells the DBA what to fix first.
  const [showRecategorizationModal, setShowRecategorizationModal] = useState(false);

  // Certification status (campaigns are blocked while any item is uncertified).
  // Drives the top-of-screen certification status banner.
  const [certificationStatus, setCertificationStatus] = useState(null);
  // Error surfaced in a WideModal (replaces blocking alert() for bulk-cert
  // failures so the full list of blocking privileges is readable).
  const [errorModal, setErrorModal] = useState(null); // { title, message } | null
  // Apply-Suggested-Levels bulk modal.
  const [showApplySuggestedModal, setShowApplySuggestedModal] = useState(false);
  const [applySuggestedCertify, setApplySuggestedCertify] = useState(false);
  const [applySuggestedRows, setApplySuggestedRows] = useState([]); // ids the DBA wants to include
  const [applySuggestedBusy, setApplySuggestedBusy] = useState(false);
  // Role-target Apply-Suggested modal (mirrors the privilege one).
  const [showApplySuggestedRoleModal, setShowApplySuggestedRoleModal] = useState(false);
  const [applySuggestedRoleRows, setApplySuggestedRoleRows] = useState([]); // role ids included
  const [applySuggestedRoleCertify, setApplySuggestedRoleCertify] = useState(false);
  const [applySuggestedRoleBusy, setApplySuggestedRoleBusy] = useState(false);
  const [suggestableRolesForModal, setSuggestableRolesForModal] = useState([]);

  // Safe timeout hook for auto-dismissing messages
  const { setSafeTimeout } = useSafeTimeout();

  // MSSQL role view mode - 'all', 'fixed', or 'user_defined'
  const [mssqlRoleViewMode, setMssqlRoleViewMode] = useState('fixed');

  // Database-specific privilege type/scope values
  const privilegeTypesByDatabase = {
    'ORACLE': [
      { value: 'SYSTEM', label: 'System' },
      { value: 'OBJECT', label: 'Object' }
    ],
    'MYSQL': [
      { value: 'GLOBAL', label: 'Global' },
      { value: 'SCHEMA', label: 'Schema' },
      { value: 'TABLE', label: 'Table' },
      { value: 'COLUMN', label: 'Column' },
      { value: 'ROUTINE', label: 'Routine' }
    ],
    'CASSANDRA': [
      { value: 'ALL', label: 'All Keyspaces' },
      { value: 'KEYSPACE', label: 'Keyspace' },
      { value: 'TABLE', label: 'Table' },
      { value: 'ROLES', label: 'Roles' },
      { value: 'FUNCTIONS', label: 'Functions' },
      { value: 'MBEAN', label: 'MBean' }
    ],
    'MSSQL': [
      { value: 'SERVER', label: 'Server' },
      { value: 'DATABASE', label: 'Database' },
      { value: 'FIXED_ROLE', label: 'Fixed Role' }
    ],
    'POSTGRESQL': [
      { value: 'GLOBAL', label: 'Global' },
      { value: 'DATABASE', label: 'Database' },
      { value: 'SCHEMA', label: 'Schema' },
      { value: 'TABLE', label: 'Table' },
      { value: 'COLUMN', label: 'Column' },
      { value: 'FUNCTION', label: 'Function' },
      { value: 'SEQUENCE', label: 'Sequence' },
      { value: 'TYPE', label: 'Type' },
      { value: 'DOMAIN', label: 'Domain' },
      { value: 'LANGUAGE', label: 'Language' },
      { value: 'LARGE_OBJECT', label: 'Large Object' },
      { value: 'TABLESPACE', label: 'Tablespace' },
      { value: 'FOREIGN_SERVER', label: 'Foreign Server' }
    ]
  };

  /* WHAT: Returns the privilege type/scope options for the currently selected database technology.
   * WHY:  Each database engine has different privilege scopes (e.g., Oracle has SYSTEM/OBJECT, MySQL has GLOBAL/SCHEMA/TABLE),
   *       so the filter dropdown must show only the relevant options. */
  const getPrivilegeTypesForCurrentDatabase = () => {
    return privilegeTypesByDatabase[selectedDatabaseType] || [];
  };

  // Database type color scheme
  const databaseTypeColors = {
    'ORACLE': {
      primary: '#e74c3c',     // Red
      light: '#fadbd8',
      border: '#e74c3c'
    },
    'MYSQL': {
      primary: '#3498db',     // Blue
      light: '#d6eaf8',
      border: '#3498db'
    },
    'POSTGRESQL': {
      primary: '#336791',     // PostgreSQL Blue
      light: '#d4e6f1',
      border: '#336791'
    },
    'MSSQL': {
      primary: '#cc2927',     // SQL Server Red
      light: '#f9e2e2',
      border: '#cc2927'
    },
    'CASSANDRA': {
      primary: '#1287b1',     // Cassandra Blue
      light: '#d1ecf1',
      border: '#1287b1'
    }
  };

  /* WHAT: Returns the color scheme for a given database technology name.
   * WHY:  Each technology has a brand-consistent color to visually distinguish tabs and badges. */
  const getDatabaseTypeColor = (dbTypeName) => {
    return databaseTypeColors[dbTypeName] || {
      primary: '#6c757d',
      light: '#e9ecef',
      border: '#6c757d'
    };
  };

  // Load database types on mount; the privilege levels are technology-specific
  // and refetched in a separate effect once selectedDatabaseType is known.
  useEffect(() => {
    fetchDatabaseTypes();
  }, []);

  // Refetch the available privilege levels (constrained by the Privilege Matrix
  // for this technology) whenever the selected technology changes.
  useEffect(() => {
    fetchPrivilegeLevels(selectedDatabaseType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDatabaseType]);

  // Auto-set database type when technology prop is provided
  useEffect(() => {
    if (technology && databaseTypes.length > 0) {
      // Convert technology prop to uppercase to match database type names
      const techUpperCase = technology.toUpperCase();
      const matchingType = databaseTypes.find(db => db.type_name === techUpperCase);
      if (matchingType && selectedDatabaseType !== techUpperCase) {
        handleDatabaseTypeChange(techUpperCase);
      }
    }
  }, [technology, databaseTypes]);

  // Load privileges when database type or filters change
  useEffect(() => {
    if (selectedDatabaseType && viewMode === 'privileges') {
      fetchPrivileges();
      fetchStatistics();
    }
  }, [selectedDatabaseType, viewMode, filters.search, filters.privilege_type, filters.privilege_level_name, filters.suggested_level_name, filters.certified, sorting.sort_by, sorting.sort_order]);

  // Load roles when view mode is roles
  useEffect(() => {
    if (selectedDatabaseType && viewMode === 'roles') {
      fetchRoles();
      fetchRoleStatistics();
    }
  }, [selectedDatabaseType, viewMode, filters.search, filters.privilege_level_name, filters.suggested_level_name, sorting.sort_by, sorting.sort_order, asOnDate]);

  // Check role support when database type changes
  useEffect(() => {
    if (selectedDatabaseType) {
      checkRoleSupport();
    }
  }, [selectedDatabaseType]);

  // Check recategorization status when database type is Oracle and view mode is privileges
  useEffect(() => {
    if (selectedDatabaseType === 'ORACLE' && viewMode === 'privileges') {
      checkRecategorizationStatus();
    }
  }, [selectedDatabaseType, viewMode]);

  // Certification status drives the banner — fetched on tech change and on
  // every view-mode change (so the Roles tab shows the same banner that surfaces
  // pending fixed-role certifications, which also block campaign creation).
  useEffect(() => {
    if (selectedDatabaseType) {
      fetchCertificationStatus(selectedDatabaseType);
    } else {
      setCertificationStatus(null);
    }
  }, [selectedDatabaseType, viewMode]);

  /* WHAT: Fetches the privilege levels available for the selected technology, narrowing the
   *       list to those enabled in the Privilege Matrix admin page when a technology is chosen.
   * WHY:  DEV_ADMINs configure which tiers are usable per-technology in the Privilege Matrix;
   *       this dropdown must respect that config so unticked tiers stop appearing as options. */
  const TIER_COLOR_MAP = {
    SUPER_ADMIN:   { color: '#6f42c1', bgColor: '#e2d9f3' },
    ADMIN:         { color: '#dc3545', bgColor: '#f8d7da' },
    WRITE_ELEVATED:{ color: '#fd7e14', bgColor: '#ffe5d0' },
    WRITE:         { color: '#ffc107', bgColor: '#fff3cd' },
    READ_ELEVATED: { color: '#0dcaf0', bgColor: '#cff4fc' },
    READ:          { color: '#0d6efd', bgColor: '#cfe2ff' },
    NONE:          { color: '#6c757d', bgColor: '#e9ecef' }
  };

  const fetchPrivilegeLevels = async (databaseType) => {
    if (databaseType) {
      try {
        const response = await axios.get(
          API_CONFIG.getDatabasesEndpoint(`privilege-matrix/${databaseType.toLowerCase()}/active-tiers/`)
        );
        if (response.data?.success && Array.isArray(response.data.tiers)) {
          const levels = response.data.tiers
            .map(t => ({
              value: t.tier_value,
              name: t.tier_name,
              ...(TIER_COLOR_MAP[t.tier_name] || { color: '#6c757d', bgColor: '#e9ecef' })
            }))
            .sort((a, b) => b.value - a.value);
          setPrivilegeLevels(levels);
          return;
        }
      } catch (error) {
        console.error('Error fetching active tiers from matrix:', error);
        // fall through to global fallback below
      }
    }
    // No technology yet, or matrix call failed — fall back to the global level list.
    try {
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint('privilege-reference/privilege-levels/'));
      if (response.data.status === 'success' && response.data.privilege_levels) {
        const levels = response.data.privilege_levels.sort((a, b) => b.value - a.value);
        setPrivilegeLevels(levels);
      }
    } catch (error) {
      console.error('Error fetching privilege levels:', error);
      setPrivilegeLevels([
        { value: 6, name: 'SUPER_ADMIN', color: '#6f42c1', bgColor: '#e2d9f3' },
        { value: 5, name: 'ADMIN', color: '#dc3545', bgColor: '#f8d7da' },
        { value: 4, name: 'WRITE_ELEVATED', color: '#fd7e14', bgColor: '#ffe5d0' },
        { value: 3, name: 'WRITE', color: '#ffc107', bgColor: '#fff3cd' },
        { value: 2, name: 'READ_ELEVATED', color: '#0dcaf0', bgColor: '#cff4fc' },
        { value: 1, name: 'READ', color: '#0d6efd', bgColor: '#cfe2ff' },
        { value: -1, name: 'NONE', color: '#6c757d', bgColor: '#e9ecef' }
      ]);
    }
  };

  /* WHAT: Fetches available database technology types and auto-selects the first one if no technology prop is given.
   * WHY:  The list of supported technologies is dynamic (driven by backend config), so it must be loaded at mount. */
  const fetchDatabaseTypes = async () => {
    try {
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint('privilege-reference/database-types/'));
      setDatabaseTypes(response.data.database_types || []);

      // Auto-select first database type only if no technology prop was provided
      if (!technology && response.data.database_types && response.data.database_types.length > 0) {
        setSelectedDatabaseType(response.data.database_types[0].type_name);
      }
    } catch (error) {
      console.error('Error fetching database types:', error);
      alert('Error fetching database types: ' + (error.response?.data?.message || error.message));
    }
  };

  /* WHAT: Fetches the privilege reference list for the selected database type with current filters and sorting.
   * WHY:  The privilege table is the primary working view; it must reflect the current filter/sort state. */
  const fetchPrivileges = async () => {
    setLoading(true);
    try {
      // Build params object, only include non-empty values
      const params = {
        database_type: selectedDatabaseType,
        ...sorting
      };

      // Add filter params only if they have values
      if (filters.search) params.search = filters.search;
      if (filters.privilege_type) params.privilege_type = filters.privilege_type;
      if (filters.privilege_level_name) params.privilege_level_name = filters.privilege_level_name;
      if (filters.suggested_level_name) params.suggested_level_name = filters.suggested_level_name;
      if (filters.certified) params.certified = filters.certified;

      const response = await axios.get(API_CONFIG.getDatabasesEndpoint('privilege-reference/privileges/'), { params });
      setPrivileges(response.data.privileges || []);
    } catch (error) {
      console.error('Error fetching privileges:', error);
      alert('Error fetching privileges: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  /* WHAT: Fetches aggregate statistics (counts by level, certification status) for the selected database type.
   * WHY:  The stat cards at the top give DBAs a quick summary of certification progress. */
  const fetchStatistics = async () => {
    try {
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint('privilege-reference/statistics/'), {
        params: { database_type: selectedDatabaseType }
      });
      setStatistics(response.data.statistics);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  /* WHAT: Fetches the role certification list for the selected database type with filters and as-on-date.
   * WHY:  Roles aggregate privileges; certifying them separately lets DBAs attest that a role's
   *       effective privilege set is appropriate, using a point-in-time snapshot (as-on-date). */
  const fetchRoles = async () => {
    setLoading(true);
    try {
      const params = {
        database_type: selectedDatabaseType,
        as_on_date: asOnDate,
        ...sorting
      };

      if (filters.search) params.search = filters.search;
      if (filters.privilege_level_name) params.privilege_level_name = filters.privilege_level_name;
      if (filters.suggested_level_name) params.suggested_level_name = filters.suggested_level_name;

      const response = await axios.get(API_CONFIG.getDatabasesEndpoint('privilege-reference/roles/'), { params });
      setRoles(response.data.roles || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
      alert('Error fetching roles: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  /* WHAT: Fetches aggregate role certification statistics for the selected database type.
   * WHY:  Provides summary counts for the role certification progress cards. */
  const fetchRoleStatistics = async () => {
    try {
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint('privilege-reference/role-statistics/'), {
        params: { database_type: selectedDatabaseType }
      });
      setRoleStatistics(response.data.statistics);
    } catch (error) {
      console.error('Error fetching role statistics:', error);
    }
  };

  /* WHAT: Checks whether the selected database type supports role certification.
   * WHY:  Not all database technologies have a role concept; if unsupported, the Roles tab is hidden
   *       and the view auto-switches to privileges. */
  const checkRoleSupport = async () => {
    try {
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint('privilege-reference/check-role-support/'), {
        params: { database_type: selectedDatabaseType }
      });
      setSupportsRoles(response.data.supports_roles);
      // Auto-switch to privileges if roles not supported
      if (!response.data.supports_roles && viewMode === 'roles') {
        setViewMode('privileges');
      }
    } catch (error) {
      console.error('Error checking role support:', error);
      setSupportsRoles(false);
    }
  };

  /* WHAT: Checks if Oracle roles need recategorization based on current privilege mappings.
   * WHY:  When privilege-level mappings change, Oracle roles' computed risk tiers may be stale;
   *       this check surfaces a banner prompting the DBA to re-run categorization. */
  const checkRecategorizationStatus = async () => {
    try {
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint('privilege-reference/check-role-recategorization/'));
      if (response.data.status === 'success') {
        setRecategorizationStatus(response.data);
      }
    } catch (error) {
      console.error('Error checking recategorization status:', error);
      setRecategorizationStatus(null);
    }
  };

  /* Certification gate fetch — uncertified mappings exclude themselves from level
   * computation (recalculator filters WHERE certified = true) and block campaign
   * creation. The banner this drives surfaces the work queue here so DBAs don't
   * discover the gate by hitting it on a campaign attempt. */
  const fetchCertificationStatus = async (dbType) => {
    const target = dbType || selectedDatabaseType;
    if (!target) return;
    try {
      const response = await axios.get(
        API_CONFIG.getDatabasesEndpoint('privilege-reference/check-certification/'),
        { params: { database_type: target } }
      );
      if (response.data.status === 'success') {
        setCertificationStatus(response.data);
      } else {
        setCertificationStatus(null);
      }
    } catch (error) {
      console.error('Error fetching certification status:', error);
      setCertificationStatus(null);
    }
  };

  /* WHAT: Triggers server-side recategorization of all Oracle roles based on current privilege mappings.
   * WHY:  After privilege-level mappings are updated, role risk tiers must be recalculated so that
   *       attestation reports and compliance dashboards reflect the new classifications. */
  const handleRunRecategorization = () => {
    setShowRecategorizationModal(true);
  };

  /* Does the actual recategorization once the DBA confirms in the modal.
   * The modal itself blocks the confirm button when there are unassigned /
   * uncertified privileges, so reaching here means the data is clean enough. */
  const confirmRunRecategorization = async () => {
    setShowRecategorizationModal(false);
    setRecategorizationLoading(true);
    setRecategorizationMessage('');

    try {
      const response = await axios.post(API_CONFIG.getDatabasesEndpoint('privilege-reference/run-role-recategorization/'));

      if (response.data.status === 'success') {
        setRecategorizationMessage(`Success: ${response.data.message}`);
        setRecategorizationStatus({ needs_recategorization: false });

        // Refresh roles data if in roles view
        if (viewMode === 'roles') {
          fetchRoles();
          fetchRoleStatistics();
        }
      } else {
        setRecategorizationMessage(`Error: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error running recategorization:', error);
      setRecategorizationMessage(`Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setRecategorizationLoading(false);

      // Auto-hide success message after 5 seconds
      setSafeTimeout(() => {
        setRecategorizationMessage('');
      }, 5000);
    }
  };

  /* WHAT: Switches the active database type and resets all view state (filters, selections, view mode).
   * WHY:  Filters and selections from one technology are not valid for another, so a clean reset prevents stale state. */
  const handleDatabaseTypeChange = (dbType) => {
    setSelectedDatabaseType(dbType);
    setSelectedItems([]);
    setViewMode('privileges'); // Reset to privileges when changing database type
    setFilters({
      privilege_type: '',
      privilege_level_name: '',
      suggested_level_name: '',
      certified: '',
      search: ''
    });
  };

  /* WHAT: Switches between privileges and roles view modes, resetting filters and selections.
   * WHY:  Privilege filters do not apply to roles and vice versa, so a clean reset is needed on toggle. */
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    setSelectedItems([]);
    setFilters({
      privilege_type: '',
      privilege_level_name: '',
      suggested_level_name: '',
      certified: '',
      search: ''
    });
  };

  /* WHAT: Parses a comma-separated privilege string and opens a detail modal listing them.
   * WHY:  Role rows show privilege counts; clicking a count opens this modal to reveal the individual
   *       privileges, enabling DBAs to drill down without leaving the certification page. */
  const handleShowPrivilegeDetails = (roleName, privilegeType, privilegeString, databaseName = null, dbid = null) => {
    if (!privilegeString) return;

    const items = privilegeString.split(',').map(item => item.trim()).filter(item => item);

    setPrivilegeDetailData({
      roleName: roleName,
      type: privilegeType,
      title: privilegeType === 'system' ? 'Direct System Privileges' :
             privilegeType === 'table' ? 'Direct Table Privileges' :
             privilegeType === 'server' ? 'Direct Server Privileges' :
             privilegeType === 'database' ? 'Direct Database Privileges' :
             privilegeType === 'inherited_server' ? 'Inherited Server Privileges' :
             privilegeType === 'inherited_database' ? 'Inherited Database Privileges' :
             'Granted Roles',
      items: items,
      parentRoleName: roleName,
      parentDatabaseName: databaseName,
      parentDbid: dbid
    });
    setShowPrivilegeDetailModal(true);
  };

  /* WHAT: Fetches full role details from the API and displays them in a drill-down modal.
   * WHY:  DBAs need to inspect a role's granted privileges, sub-roles, and metadata before certifying
   *       its privilege level; this async fetch keeps the main table lightweight. */
  const handleShowRoleDetails = async (roleName, databaseName = null, dbid = null) => {
    setRoleDetailLoading(true);
    setShowRoleDetailModal(true);

    try {
      const params = {
        database_type: selectedDatabaseType,
        role_name: roleName
      };

      if (databaseName) params.database_name = databaseName;
      if (dbid) params.dbid = dbid;

      const response = await axios.get(API_CONFIG.getDatabasesEndpoint('privilege-reference/role-details/'), { params });

      if (response.data.status === 'success') {
        setRoleDetailData(response.data.role);
      } else {
        console.error('Error fetching role details:', response.data.message);
        setRoleDetailData(null);
      }
    } catch (error) {
      console.error('Error fetching role details:', error);
      setRoleDetailData(null);
    } finally {
      setRoleDetailLoading(false);
    }
  };

  /* WHAT: Updates a single filter field and clears the item selection.
   * WHY:  Changing a filter invalidates the current selection since the visible rows change. */
  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setSelectedItems([]);
  };

  /* WHAT: Resets all filters to their default (empty) state and clears the item selection.
   * WHY:  Provides a one-click way to return to the unfiltered view. */
  const handleClearAllFilters = () => {
    setFilters({
      privilege_type: '',
      privilege_level_name: '',
      suggested_level_name: '',
      certified: '',
      search: ''
    });
    setSelectedItems([]);
  };

  /* WHAT: Toggles sort direction on the same field or sets a new sort field with descending default.
   * WHY:  Clickable column headers need toggle behavior for intuitive table sorting. */
  const handleSortChange = (field) => {
    if (sorting.sort_by === field) {
      // Toggle order if same field
      setSorting({
        sort_by: field,
        sort_order: sorting.sort_order === 'asc' ? 'desc' : 'asc'
      });
    } else {
      // New field, default to desc
      setSorting({
        sort_by: field,
        sort_order: 'desc'
      });
    }
  };

  /* WHAT: Toggles a single privilege's selection state for bulk operations.
   * WHY:  Checkbox-based multi-select is the standard pattern for bulk certify/level-change actions. */
  const handleSelectItem = (id) => {
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter(item => item !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };

  /* WHAT: Toggles selection of all currently visible privileges (select all / deselect all).
   * WHY:  Enables bulk operations across the entire filtered result set with one click. */
  const handleSelectAll = () => {
    if (selectedItems.length === privileges.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(privileges.map(item => item.id));
    }
  };

  /* WHAT: Updates the certification status of a single privilege and refreshes the list.
   * WHY:  Individual certification lets DBAs approve one privilege at a time for careful review. */
  const handleUpdateCertification = async (privilegeId, certified) => {
    try {
      await axios.post(API_CONFIG.getDatabasesEndpoint('privilege-reference/update-certification/'), {
        database_type: selectedDatabaseType,
        privilege_id: privilegeId,
        certified: certified,
        certified_by: certified ? currentUser : null
      });

      alert(`Privilege ${certified ? 'certified' : 'uncertified'} successfully!`);
      fetchPrivileges();
      fetchStatistics();
      fetchCertificationStatus();
    } catch (error) {
      console.error('Error updating certification:', error);
      alert('Error updating certification: ' + (error.response?.data?.message || error.message));
    }
  };

  /* WHAT: Certifies or uncertifies all selected privileges in a single API call.
   * WHY:  Bulk certification dramatically speeds up the initial review of hundreds of privileges
   *       by letting DBAs apply a decision to an entire filtered set at once. */
  const handleBulkUpdateCertification = async () => {
    console.log('handleBulkUpdateCertification called');
    console.log('selectedItems:', selectedItems);
    console.log('bulkCertifyAction:', bulkCertifyAction);
    console.log('selectedDatabaseType:', selectedDatabaseType);
    console.log('currentUser:', currentUser);

    if (selectedItems.length === 0) {
      alert('Please select at least one privilege to update');
      return;
    }

    if (!bulkCertifyAction) {
      alert('Please select an action (Certify or Uncertify)');
      return;
    }

    const certified = bulkCertifyAction === 'certify';
    const action = certified ? 'certify' : 'uncertify';

    // Removed confirmation dialog due to browser popup blocking issues
    // User has already made their selection, so proceed directly
    console.log(`Proceeding to ${action} ${selectedItems.length} privilege(s)`);
    console.log('Sending API request...');
    try {
      const response = await axios.post(API_CONFIG.getDatabasesEndpoint('privilege-reference/bulk-update-certification/'), {
        database_type: selectedDatabaseType,
        privilege_ids: selectedItems,
        certified: certified,
        certified_by: certified ? currentUser : null
      });

      console.log('API response:', response.data);
      alert(`Successfully ${action}ied ${selectedItems.length} privilege(s)!`);
      setSelectedItems([]);
      setBulkCertifyAction('');
      fetchPrivileges();
      fetchStatistics();
      fetchCertificationStatus();
    } catch (error) {
      console.error('Error bulk updating certification:', error);
      console.error('Error response:', error.response);
      setErrorModal({
        title: 'Certification Blocked',
        message: error.response?.data?.message || error.message
      });
    }
  };

  /* WHAT: Changes the privilege level of all selected privileges to a chosen tier in one API call.
   * WHY:  When a DBA reclassifies a group of related privileges (e.g., all object-level writes),
   *       bulk level change avoids tedious one-by-one edits. */
  const handleBulkUpdateLevel = async () => {
    if (selectedItems.length === 0) {
      alert('Please select at least one privilege to update');
      return;
    }

    if (!bulkLevelChange) {
      alert('Please select a privilege level');
      return;
    }

    const level = privilegeLevels.find(l => l.value === parseInt(bulkLevelChange));
    if (!level) {
      alert('Invalid privilege level selected');
      return;
    }

    if (!window.confirm(`Change ${selectedItems.length} privilege(s) to level ${level.name}?`)) {
      return;
    }

    try {
      const response = await axios.post(API_CONFIG.getDatabasesEndpoint('privilege-reference/bulk-update-level/'), {
        database_type: selectedDatabaseType,
        privilege_ids: selectedItems,
        privilege_level: level.value,
        privilege_level_name: level.name,
        notes: `Bulk updated to ${level.name}`,
        certified_by: currentUser
      });

      alert(`Successfully updated ${selectedItems.length} privilege(s) to level ${level.name}!`);
      setSelectedItems([]);
      setBulkLevelChange('');
      fetchPrivileges();
      fetchStatistics();
    } catch (error) {
      console.error('Error bulk updating privilege levels:', error);
      alert('Error bulk updating privilege levels: ' + (error.response?.data?.message || error.message));
    }
  };

  /* WHAT: The subset of currently-selected privileges that are UNASSIGNED and
   *       have a documentation suggestion — the rows the Apply-Suggested modal
   *       can act on.
   * WHY:  Only UNASSIGNED rows take a suggestion (an already-assigned level is
   *       never overwritten); rows without a suggestion can't be auto-applied. */
  const suggestableSelected = privileges.filter(
    p => selectedItems.includes(p.id)
      && p.privilege_level_name === 'UNASSIGNED'
      && p.suggested_level_name
  );

  /* WHAT: Opens the Apply-Suggested-Levels modal, defaulting every eligible
   *       selected row to "included". */
  const openApplySuggestedModal = () => {
    if (suggestableSelected.length === 0) {
      setErrorModal({
        title: 'No Suggestible Items',
        message: 'None of the selected items are UNASSIGNED with a suggested level. '
               + 'Filter to UNASSIGNED items that have a Suggested Level, select them, then try again.'
      });
      return;
    }
    setApplySuggestedRows(suggestableSelected.map(p => p.id));
    setApplySuggestedCertify(false);
    setShowApplySuggestedModal(true);
  };

  /* WHAT: Calls the bulk-apply-suggested endpoint for the included rows,
   *       optionally certifying them too. */
  const handleApplySuggestedLevels = async () => {
    if (applySuggestedRows.length === 0) {
      setErrorModal({ title: 'Nothing Selected', message: 'Include at least one row to apply.' });
      return;
    }
    setApplySuggestedBusy(true);
    try {
      const response = await axios.post(
        API_CONFIG.getDatabasesEndpoint('privilege-reference/bulk-apply-suggested/'),
        {
          database_type: selectedDatabaseType,
          privilege_ids: applySuggestedRows,
          certify: applySuggestedCertify,
          certified_by: applySuggestedCertify ? currentUser : null
        }
      );
      setShowApplySuggestedModal(false);
      setSelectedItems([]);
      alert(response.data.message || 'Suggested levels applied.');
      fetchPrivileges();
      fetchStatistics();
      fetchCertificationStatus();
    } catch (error) {
      console.error('Error applying suggested levels:', error);
      setErrorModal({
        title: 'Apply Suggested Levels Failed',
        message: error.response?.data?.message || error.message
      });
    } finally {
      setApplySuggestedBusy(false);
    }
  };

  /* WHAT: Opens the edit modal pre-populated with the selected privilege's current level and notes.
   * WHY:  Pre-populating saves the DBA from re-entering existing values when making incremental changes. */
  const handleOpenEditModal = (privilege) => {
    setEditingPrivilege(privilege);
    setEditForm({
      privilege_level: privilege.privilege_level,
      privilege_level_name: privilege.privilege_level_name,
      notes: privilege.notes || ''
    });
    setShowEditModal(true);
  };

  /* WHAT: Closes the edit privilege modal and resets the form state.
   * WHY:  Resetting prevents stale data from appearing if the modal is reopened for a different privilege. */
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingPrivilege(null);
    setEditForm({
      privilege_level: 0,
      privilege_level_name: '',
      notes: ''
    });
  };

  /* WHAT: Updates the edit form's privilege level and name when the user selects a new level from the dropdown.
   * WHY:  Level value and name must stay in sync because the API expects both. */
  const handleEditLevelChange = (levelValue) => {
    const level = privilegeLevels.find(l => l.value === parseInt(levelValue));
    if (level) {
      setEditForm({
        ...editForm,
        privilege_level: level.value,
        privilege_level_name: level.name
      });
    }
  };

  /* WHAT: Saves the edited privilege level to the API and refreshes the list.
   * WHY:  Persists the DBA's reclassification decision and updates statistics to reflect the change. */
  const handleSavePrivilegeLevel = async () => {
    if (!editingPrivilege) return;

    try {
      await axios.post(API_CONFIG.getDatabasesEndpoint('privilege-reference/update-level/'), {
        database_type: selectedDatabaseType,
        privilege_id: editingPrivilege.id,
        privilege_level: editForm.privilege_level,
        privilege_level_name: editForm.privilege_level_name,
        notes: editForm.notes,
        certified_by: currentUser
      });

      alert(`Successfully updated privilege level for ${editingPrivilege.privilege_name}!`);
      handleCloseEditModal();
      fetchPrivileges();
      fetchStatistics();
    } catch (error) {
      console.error('Error updating privilege level:', error);
      alert('Error updating privilege level: ' + (error.response?.data?.message || error.message));
    }
  };

  /* WHAT: Looks up the color scheme for a privilege level name from the loaded level definitions.
   * WHY:  Consistent color-coding across badges and stat cards depends on a single source of truth for colors. */
  const getLevelColor = (levelName) => {
    // UNASSIGNED is the -1 sentinel (discovered, no DBA-assigned level). It is
    // not in privilegeLevels (a real tier list); render it red to match the
    // UNASSIGNED treatment on the privileges side.
    if (levelName === 'UNASSIGNED') {
      return { color: '#dc3545', bgColor: '#f8d7da' };
    }
    const level = privilegeLevels.find(l => l.name === levelName);
    return level || { color: '#6c757d', bgColor: '#e9ecef' };
  };

  // ============================================
  // ROLE CERTIFICATION HANDLERS (MSSQL)
  // ============================================

  /* WHAT: Certifies all selected roles in a single API call.
   * WHY:  Bulk role certification speeds up periodic reviews when many roles are unchanged. */
  /* WHAT: Opens the role Apply-Suggested review modal for the selected
   *       UNASSIGNED predefined roles that have a documentation suggestion.
   * WHY:  Mirrors the privileges-tab Apply-Suggested modal so a DBA can
   *       review the per-role suggestions, optionally certify, then apply. */
  const handleApplySuggestedRoleLevels = (suggestableRoles) => {
    if (!suggestableRoles || suggestableRoles.length === 0) {
      setErrorModal({
        title: 'No Suggestible Roles',
        message: 'None of the selected roles are UNASSIGNED with a suggested level.'
      });
      return;
    }
    setSuggestableRolesForModal(suggestableRoles);
    setApplySuggestedRoleRows(suggestableRoles.map(r => r.id));
    setApplySuggestedRoleCertify(false);
    setShowApplySuggestedRoleModal(true);
  };

  /* WHAT: Calls bulk-apply-suggested (target='role') for the included roles,
   *       optionally certifying them too. */
  const handleApplySuggestedRolesConfirm = async () => {
    if (applySuggestedRoleRows.length === 0) {
      setErrorModal({ title: 'Nothing Selected', message: 'Include at least one role to apply.' });
      return;
    }
    setApplySuggestedRoleBusy(true);
    try {
      const response = await axios.post(
        API_CONFIG.getDatabasesEndpoint('privilege-reference/bulk-apply-suggested/'),
        {
          database_type: selectedDatabaseType,
          target: 'role',
          privilege_ids: applySuggestedRoleRows,
          certify: applySuggestedRoleCertify,
          certified_by: applySuggestedRoleCertify ? currentUser : null
        }
      );
      setShowApplySuggestedRoleModal(false);
      setSelectedRoles([]);
      alert(response.data.message || 'Suggested levels applied to roles.');
      fetchRoles();
      fetchRoleStatistics();
      fetchCertificationStatus();
    } catch (error) {
      console.error('Error applying suggested role levels:', error);
      setErrorModal({
        title: 'Apply Suggested Levels Failed',
        message: error.response?.data?.message || error.message
      });
    } finally {
      setApplySuggestedRoleBusy(false);
    }
  };

  const handleBulkCertifyRoles = async () => {
    if (selectedRoles.length === 0) {
      alert('Please select at least one role to certify');
      return;
    }

    // Guard: a role with no DBA-assigned level (UNASSIGNED) cannot be
    // certified — same rule as privileges. The backend also rejects this,
    // but block it here with a clear message before the request.
    const unassignedSelected = roles.filter(
      r => selectedRoles.includes(r.id) && r.privilege_level_name === 'UNASSIGNED'
    );
    if (unassignedSelected.length > 0) {
      const names = unassignedSelected.slice(0, 5).map(r => r.role_name).join(', ');
      const more = unassignedSelected.length > 5 ? ` (+${unassignedSelected.length - 5} more)` : '';
      setErrorModal({
        title: 'Certification Blocked',
        message: `${unassignedSelected.length} selected role(s) are UNASSIGNED and cannot be certified until a level is assigned: ${names}${more}`
      });
      return;
    }

    if (!window.confirm(`Are you sure you want to certify ${selectedRoles.length} role(s)?`)) {
      return;
    }

    try {
      // MSSQL fixed roles carry a FIXED_ROLE reference id (global-only); flag the
      // scope so the backend resolves it against the global reference. Only fixed
      // roles are certifiable here, so for MSSQL the selection is always fixed.
      const certReqData = {
        database_type: selectedDatabaseType,
        role_ids: selectedRoles,
        certified: true,
        certified_by: currentUser
      };
      const certTargets = roles.filter(r => selectedRoles.includes(r.id));
      if (selectedDatabaseType === 'MSSQL' && certTargets.some(r => r && (r.is_fixed_role || r.role_scope === 'FIXED_ROLE'))) {
        certReqData.role_scope = 'FIXED_ROLE';
      }
      const response = await axios.post(API_CONFIG.getDatabasesEndpoint('privilege-reference/update-role-certification/'), certReqData);

      alert(`Successfully certified ${response.data.updated_count} role(s)!`);
      if (response.data.warning) {
        alert('Note: ' + response.data.warning);
      }
      setSelectedRoles([]);
      fetchRoles();
      fetchRoleStatistics();
      fetchCertificationStatus();
    } catch (error) {
      console.error('Error certifying roles:', error);
      alert('Error certifying roles: ' + (error.response?.data?.message || error.message));
    }
  };

  /* WHAT: Opens the role-level edit modal in bulk mode for all selected roles.
   * WHY:  Allows DBAs to reclassify multiple roles to the same privilege tier simultaneously. */
  const handleBulkChangeRoleLevel = () => {
    if (selectedRoles.length === 0) {
      alert('Please select at least one role to change level');
      return;
    }

    // Open modal in bulk mode
    setEditingRole({ id: 'bulk', name: `${selectedRoles.length} selected roles`, is_bulk: true });
    setEditRoleForm({
      privilege_level: '',
      privilege_level_name: '',
      notes: '',
      certify_after_update: false
    });
    setShowEditRoleLevelModal(true);
  };

  /* WHAT: Opens the role-level edit modal pre-populated with the selected role's current level and notes.
   * WHY:  Pre-populating saves the DBA from re-entering existing values for single-role edits. */
  const handleOpenEditRoleLevelModal = (role) => {
    setEditingRole(role);
    setEditRoleForm({
      privilege_level: role.privilege_level,
      privilege_level_name: role.privilege_level_name,
      notes: role.notes || '',
      certify_after_update: false
    });
    setShowEditRoleLevelModal(true);
  };

  /* WHAT: Closes the role-level edit modal and resets its form state.
   * WHY:  Resetting prevents stale data from appearing if the modal is reopened for a different role. */
  const handleCloseEditRoleLevelModal = () => {
    setShowEditRoleLevelModal(false);
    setEditingRole(null);
    setEditRoleForm({
      privilege_level: '',
      privilege_level_name: '',
      notes: '',
      certify_after_update: false
    });
  };

  /* WHAT: Updates the role edit form's privilege level and name when a new level is selected.
   * WHY:  Level value and name must stay in sync because the API expects both. */
  const handleEditRoleLevelChange = (levelValue) => {
    const level = privilegeLevels.find(l => l.value === parseInt(levelValue));
    if (level) {
      setEditRoleForm({
        ...editRoleForm,
        privilege_level: level.value,
        privilege_level_name: level.name
      });
    }
  };

  /* WHAT: Saves the edited role privilege level (single or bulk) to the API and refreshes the list.
   * WHY:  Persists the DBA's role-level decision, optionally certifying the role in the same operation
   *       to reduce round-trips during batch reviews. */
  const handleSaveRoleLevel = async () => {
    if (!editingRole) return;

    if (!editRoleForm.privilege_level) {
      alert('Please select a privilege level');
      return;
    }

    try {
      const requestData = {
        database_type: selectedDatabaseType,
        privilege_level: editRoleForm.privilege_level,
        privilege_level_name: editRoleForm.privilege_level_name,
        notes: editRoleForm.notes,
        certify_after_update: editRoleForm.certify_after_update
      };

      if (editRoleForm.certify_after_update) {
        requestData.certified_by = currentUser;
      }

      // Bulk or single update
      if (editingRole.is_bulk) {
        requestData.role_ids = selectedRoles;
      } else {
        requestData.role_id = editingRole.id;
      }

      // MSSQL fixed roles are GLOBAL-only: their `id` is a FIXED_ROLE reference
      // row id, so the backend must resolve it against the global reference, not
      // the per-db role table. Flag the scope so it routes correctly. Only fixed
      // roles are editable here (user-defined/public are read-only derived rows),
      // so for MSSQL a fixed-role edit always carries this scope.
      const targetRoles = editingRole.is_bulk
        ? roles.filter(r => selectedRoles.includes(r.id))
        : [editingRole];
      if (selectedDatabaseType === 'MSSQL' && targetRoles.some(r => r && (r.is_fixed_role || r.role_scope === 'FIXED_ROLE'))) {
        requestData.role_scope = 'FIXED_ROLE';
      }

      const response = await axios.post(API_CONFIG.getDatabasesEndpoint('privilege-reference/update-role-level/'), requestData);

      const count = editingRole.is_bulk ? response.data.updated_count : 1;
      alert(`Successfully updated privilege level for ${count} role(s)!`);

      if (response.data.warning) {
        alert('Note: ' + response.data.warning);
      }

      handleCloseEditRoleLevelModal();
      setSelectedRoles([]);
      fetchRoles();
      fetchRoleStatistics();
      fetchCertificationStatus();
    } catch (error) {
      console.error('Error updating role level:', error);
      alert('Error updating role level: ' + (error.response?.data?.message || error.message));
    }
  };

  /* Renders a color-coded text label for a privilege level using the technology's
     active tier set (driven by the Privilege Matrix). */
  const PrivilegeLevelBadge = ({ levelName }) => {
    const { color } = getLevelColor(levelName);
    return (
      <span style={{ color: color, fontWeight: '500' }}>
        {levelName}
      </span>
    );
  };

  /* WHAT: Renders a compact summary card with a colored left border, used for privilege/role statistics.
   * WHY:  Stat cards give DBAs an at-a-glance view of certification progress and level distribution. */
  const StatCard = ({ title, value, color }) => (
    <div style={{
      background: 'white',
      borderRadius: '6px',
      padding: '8px 12px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
      borderLeft: `3px solid ${color}`,
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: '8px'
    }}>
      <div style={{ fontSize: '0.72rem', color: '#6c757d', fontWeight: '500' }}>{title}</div>
      <div style={{ fontSize: '1.25rem', fontWeight: '700', color: color, lineHeight: '1.1' }}>{value}</div>
    </div>
  );

  /* WHAT: Returns an ascending/descending arrow indicator for sortable column headers.
   * WHY:  Visual sort direction feedback lets users know which column is sorted and in which order. */
  const getSortIcon = (field) => {
    if (sorting.sort_by !== field) return null;
    return sorting.sort_order === 'asc' ? ' ▲' : ' ▼';
  };

  // Technologies migrated to the unified CertificationWorkspace. These bypass
  // the legacy stats-cards + filter-bar + table UI below; only PostgreSQL still
  // uses the legacy path. (Cassandra has no separate Roles workspace because
  // every Cassandra role is pure-derived from its grants — see CASSANDRA Roles
  // block below for the read-only derived treatment.)
  const usesWorkspace =
    selectedDatabaseType === 'MSSQL' ||
    selectedDatabaseType === 'ORACLE' ||
    selectedDatabaseType === 'MYSQL' ||
    selectedDatabaseType === 'CASSANDRA';

  return (
    <div style={{ padding: '30px', background: '#f8f9fa', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1800px', margin: '0 auto' }}>
        {/* Header — Oracle's "Recategorize Roles" control lives here in the
            top frame (single location; the old in-workspace header button and
            the legacy table-header button were removed). It is disabled when
            no recategorization is needed, and the confirm modal further blocks
            the run while any privilege is unassigned/uncertified. */}
        <div style={{ marginBottom: '30px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '20px' }}>
          <div>
            <h2 style={{ fontSize: '2rem', fontWeight: '700', color: '#1a1a2e', marginBottom: '10px' }}>
              <i className="fas fa-database" style={{ marginRight: '12px', color: '#667eea' }}></i>
              {technology ? `${technology.charAt(0).toUpperCase() + technology.slice(1)} Privilege Certification` : 'Manage Privilege Certification'}
            </h2>
            <p style={{ color: '#6c757d', fontSize: '1rem', marginBottom: 0 }}>
              {technology
                ? `View and manage ${technology.toUpperCase()} privilege reference tables and certification status`
                : 'View and manage database privilege reference tables and certification status'}
            </p>
          </div>
          {selectedDatabaseType === 'ORACLE' && (() => {
            const needs = recategorizationStatus?.needs_recategorization;
            const disabled = recategorizationLoading || !needs;
            return (
              <button
                onClick={handleRunRecategorization}
                disabled={disabled}
                style={{
                  padding: '12px 24px',
                  background: needs ? '#ffc107' : '#e9ecef',
                  color: needs ? '#000' : '#adb5bd',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s',
                  fontSize: '0.95rem',
                  opacity: recategorizationLoading ? 0.6 : 1,
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
                title={needs
                  ? 'Recategorization needed — click to review and run'
                  : 'No recategorization needed — Oracle roles are up to date with current privilege mappings'}
              >
                {recategorizationLoading ? (
                  <><i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>Running...</>
                ) : (
                  <>
                    <i className="fas fa-sync-alt" style={{ marginRight: '8px' }}></i>
                    Recategorize Roles
                    {needs && <i className="fas fa-exclamation-circle" style={{ marginLeft: '8px' }}></i>}
                  </>
                )}
              </button>
            );
          })()}
        </div>

        {/* Database Type Selection - Hidden when technology prop is provided */}
        {!technology && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '25px', marginBottom: '25px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <h4 style={{ fontSize: '1.2rem', fontWeight: '600', marginBottom: '15px', color: '#1a1a2e' }}>
              <i className="fas fa-server" style={{ marginRight: '8px', color: '#667eea' }}></i>
              Select Database Type
            </h4>
            <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
              {databaseTypes.map(dbType => {
                const colors = getDatabaseTypeColor(dbType.type_name);
                const isSelected = selectedDatabaseType === dbType.type_name;
                return (
                  <button
                    key={dbType.type_name}
                    onClick={() => handleDatabaseTypeChange(dbType.type_name)}
                    style={{
                      padding: '15px 30px',
                      borderRadius: '8px',
                      border: `2px solid ${isSelected ? colors.border : '#e9ecef'}`,
                      background: isSelected ? colors.light : 'white',
                      fontWeight: '600',
                      color: isSelected ? colors.primary : '#6c757d',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      fontSize: '1rem'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = colors.border;
                        e.currentTarget.style.backgroundColor = colors.light;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = '#e9ecef';
                        e.currentTarget.style.backgroundColor = 'white';
                      }
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <i className="fas fa-database" style={{ color: colors.primary }}></i>
                      <div>
                        <div>{dbType.type_name}</div>
                        <div style={{ fontSize: '0.75rem', marginTop: '5px', opacity: 0.8 }}>
                          {dbType.privilege_count} privileges
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Privileges/Roles Tabs */}
        {selectedDatabaseType && supportsRoles && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '25px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', gap: '15px', borderBottom: '2px solid #e9ecef', paddingBottom: '10px' }}>
              <button
                onClick={() => handleViewModeChange('privileges')}
                style={{
                  padding: '12px 30px',
                  border: 'none',
                  borderBottom: viewMode === 'privileges' ? '3px solid #667eea' : '3px solid transparent',
                  background: 'transparent',
                  fontWeight: '600',
                  color: viewMode === 'privileges' ? '#667eea' : '#6c757d',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  fontSize: '1rem'
                }}
              >
                <i className="fas fa-key" style={{ marginRight: '8px' }}></i>
                Privileges
              </button>
              <button
                onClick={() => handleViewModeChange('roles')}
                style={{
                  padding: '12px 30px',
                  border: 'none',
                  borderBottom: viewMode === 'roles' ? '3px solid #667eea' : '3px solid transparent',
                  background: 'transparent',
                  fontWeight: '600',
                  color: viewMode === 'roles' ? '#667eea' : '#6c757d',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  fontSize: '1rem'
                }}
              >
                <i className="fas fa-users-cog" style={{ marginRight: '8px' }}></i>
                Roles
              </button>
            </div>
          </div>
        )}

        {/* Certification Status Banner — placed directly under the
         * Privileges/Roles tabs (above the workspace) so the campaign-blocking
         * status is the first thing a DBA sees. Shows on both tabs because
         * campaign creation is blocked by uncertified items in either (MSSQL
         * fixed roles live in mssql_role_categorization, not the mapping
         * table, so a Roles-tab user must see the same blocker).
         * Red when any privilege/role is uncertified; green when fully certified. */}
        {selectedDatabaseType && certificationStatus && (
          (certificationStatus.uncertified_count > 0 || (certificationStatus.role_uncertified_count || 0) > 0) ? (
            <div style={{
              background: '#f8d7da',
              border: '2px solid #dc3545',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '20px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              {(() => {
                // A privilege must have a DBA-assigned level before it can be
                // certified. While any active privilege is still UNASSIGNED
                // (privilege_level = -1), the View & Certify flow is blocked:
                // certifying a level-less privilege is meaningless. The DBA must
                // first assign levels (inline in the table below), then certify.
                const unassignedCount = certificationStatus.unassigned_count || 0;
                const hasUnassigned = unassignedCount > 0;
                const privUncert = certificationStatus.uncertified_count || 0;
                const roleTotal = certificationStatus.role_total_count || 0;
                const roleCert = certificationStatus.role_certified_count || 0;
                const roleUncert = certificationStatus.role_uncertified_count || 0;
                // Fixed roles with no level yet: a subset of roleUncert that
                // must be ASSIGNED before they can be certified.
                const roleUnassigned = certificationStatus.role_unassigned_count || 0;
                // Roles that have a level but aren't certified yet (just certify).
                const roleCertOnly = Math.max(roleUncert - roleUnassigned, 0);
                const totalPending = privUncert + roleUncert;
                return (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ flex: 1, color: '#721c24', fontSize: '0.95rem', lineHeight: 1.4 }}>
                  <i className="fas fa-exclamation-circle" style={{ marginRight: '8px' }}></i>
                  <strong>{selectedDatabaseType} campaign creation blocked — {totalPending} action item{totalPending === 1 ? '' : 's'} pending.</strong>
                  {' '}Privileges: <strong>{certificationStatus.certified_count}/{certificationStatus.total_count}</strong> certified
                  {roleTotal > 0 && <> · Roles: <strong>{roleCert}/{roleTotal}</strong> certified</>}.
                  {' '}{hasUnassigned
                    ? <>Assign a level to <strong>{unassignedCount}</strong> privilege{unassignedCount === 1 ? '' : 's'}, then certify.</>
                    : privUncert > 0
                      ? <>Certify the pending privileges to unblock campaigns.</>
                      : null}
                  {roleUnassigned > 0 && <> Assign a level to <strong>{roleUnassigned}</strong> role{roleUnassigned === 1 ? '' : 's'} on the <strong>Roles</strong> tab, then certify.</>}
                  {roleCertOnly > 0 && <> Certify <strong>{roleCertOnly}</strong> role{roleCertOnly === 1 ? '' : 's'} on the <strong>Roles</strong> tab.</>}
                  {selectedDatabaseType === 'ORACLE' && viewMode === 'roles' && (
                    <> {' '}<em>Oracle role privilege levels are derived from
                    their component privileges and may not be correct until
                    all the privileges are certified.</em></>
                  )}
                </div>
              </div>
                );
              })()}
            </div>
          ) : certificationStatus.total_count > 0 && (
            // Everything is certified. For Oracle, roles are DERIVED from their
            // component privileges, so a pending recategorization means the
            // currently-shown derived role levels are stale — campaigns must NOT
            // be advertised as ready until the DBA reruns recategorization. In
            // that case suppress the green all-clear here; the amber
            // "Role Recategorization Needed" banner below carries the message.
            !(selectedDatabaseType === 'ORACLE' && recategorizationStatus?.needs_recategorization) && (
            <div style={{
              background: '#d4edda',
              border: '2px solid #28a745',
              borderRadius: '12px',
              padding: '12px 20px',
              marginBottom: '20px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <i className="fas fa-check-circle" style={{ marginRight: '12px', color: '#155724', fontSize: '1.2rem' }}></i>
                <span style={{ color: '#155724', fontSize: '0.95rem' }}>
                  All {certificationStatus.total_count} {selectedDatabaseType} privileges
                  {(certificationStatus.role_total_count || 0) > 0
                    ? ` and ${certificationStatus.role_total_count} roles`
                    : ''} certified — campaigns can be created.
                </span>
              </div>
            </div>
            )
          )
        )}

        {/* Role Recategorization Banner — Oracle only. Moved here (directly
            under the certification status banner, above the stat cards) so the
            "Role Recategorization Needed" prompt is at the TOP of the page
            rather than buried below the stat cards. Oracle role levels are
            DERIVED from component privileges; when a privilege level/cert
            changes, the derived role levels go stale until recategorization is
            rerun — so this must be prominent. The Recategorize Roles action
            button lives in the page top frame (single location). */}
        {selectedDatabaseType === 'ORACLE' && viewMode === 'privileges' && recategorizationStatus?.needs_recategorization && (
          <div style={{
            background: '#fff3cd',
            border: '2px solid #ffc107',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div style={{ flex: 1 }}>
              <h5 style={{ color: '#856404', marginBottom: '8px', fontSize: '1.1rem', fontWeight: '600' }}>
                <i className="fas fa-exclamation-triangle" style={{ marginRight: '8px' }}></i>
                Role Recategorization Needed
              </h5>
              <p style={{ color: '#856404', marginBottom: '8px', fontSize: '0.95rem' }}>
                {recategorizationStatus.change_description}
              </p>
              <p style={{ color: '#856404', marginBottom: '8px', fontSize: '0.85rem', opacity: 0.8 }}>
                Last change: {new Date(recategorizationStatus.last_mapping_change).toLocaleString()}
              </p>
              <p style={{ color: '#856404', marginBottom: '0', fontSize: '0.9rem', fontWeight: '600' }}>
                Use the <strong>Recategorize Roles</strong> button at the top of the page to apply.
              </p>
            </div>
          </div>
        )}

        {/* MSSQL Privileges — unified Certification Workspace (replaces the
            old stats-cards + filter-bar + table + modals for MSSQL). Other
            technologies still use the legacy table UI below. */}
        {selectedDatabaseType === 'MSSQL' && viewMode === 'privileges' && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '25px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CertificationWorkspace
              databaseType="MSSQL"
              target="privilege"
              privilegeLevels={privilegeLevels}
              currentUser={currentUser}
              onDataChanged={() => {
                fetchCertificationStatus('MSSQL');
                fetchStatistics();
              }}
            />
          </div>
        )}

        {/* MySQL Privileges — unified Certification Workspace. MySQL has the
            same privilege ref shape as Oracle/MSSQL (mysql_privilege_mapping_reference
            with certified + suggested_level columns), so the workspace works
            generically. Roles are read-only DERIVED (see MySQL Roles block below). */}
        {selectedDatabaseType === 'MYSQL' && viewMode === 'privileges' && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '25px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CertificationWorkspace
              databaseType="MYSQL"
              target="privilege"
              privilegeLevels={privilegeLevels}
              currentUser={currentUser}
              onDataChanged={() => {
                fetchCertificationStatus('MYSQL');
                fetchStatistics();
              }}
            />
          </div>
        )}

        {/* Oracle Privileges — same unified Certification Workspace as MSSQL.
            The "Recategorize Roles" control now lives in the page top frame
            (single location), so the workspace renders without a local header. */}
        {selectedDatabaseType === 'ORACLE' && viewMode === 'privileges' && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '25px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CertificationWorkspace
              databaseType="ORACLE"
              target="privilege"
              privilegeLevels={privilegeLevels}
              currentUser={currentUser}
              onDataChanged={() => {
                fetchCertificationStatus('ORACLE');
                fetchStatistics();
                // A privilege level/cert change sets the DB
                // role_recategorization_flag (trigger). Re-fetch so the
                // top-frame Recategorize Roles button enables immediately.
                checkRecategorizationStatus();
              }}
            />
          </div>
        )}

        {/* Statistics Dashboard - Privileges */}
        {!usesWorkspace && selectedDatabaseType && viewMode === 'privileges' && statistics && (
          <>
            {/* First row - General statistics */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '12px',
              marginBottom: '12px'
            }}>
              <StatCard
                title="Total Privileges"
                value={statistics.total_privileges}
                color="#667eea"
              />
              <StatCard
                title="Certified"
                value={statistics.certification_status?.certified || 0}
                color="#28a745"
              />
              <StatCard
                title="Uncertified"
                value={statistics.certification_status?.uncertified || 0}
                color="#ffc107"
              />
              <StatCard
                title="Unassigned"
                value={statistics.certification_status?.unassigned || 0}
                color="#dc3545"
              />
            </div>

            {/* Second row - Privilege level breakdown (if available) */}
            {statistics.by_level && Object.keys(statistics.by_level).length > 0 && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                gap: '10px',
                marginBottom: '20px'
              }}>
                {Object.entries(statistics.by_level || {}).map(([level, count]) => {
                  const levelColor = getLevelColor(level).color;
                  return (
                    <StatCard
                      key={level}
                      title={level}
                      value={count}
                      color={levelColor}
                    />
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* MSSQL Roles — unified Certification Workspace (replaces the legacy
            role stats + filter + table + modals for MSSQL). */}
        {selectedDatabaseType === 'MSSQL' && viewMode === 'roles' && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '25px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CertificationWorkspace
              databaseType="MSSQL"
              target="role"
              privilegeLevels={privilegeLevels}
              currentUser={currentUser}
              onDataChanged={() => {
                fetchCertificationStatus('MSSQL');
                fetchRoleStatistics();
              }}
            />
          </div>
        )}

        {/* MySQL Roles — DERIVED (read-only) Certification Workspace, same
            model as Oracle. mysql_privilege_role_categories has no certified
            column — every role's level is computed from its component
            privileges via MySQLRoleCategorizer. So `derived` mode hides the
            Suggested column + assign/certify/unassign actions + bulk bar. */}
        {selectedDatabaseType === 'MYSQL' && viewMode === 'roles' && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '25px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div style={{
              background: '#e7f1ff', border: '1px solid #b3d9ff',
              borderRadius: '8px', padding: '10px 14px', marginBottom: '14px',
              fontSize: '0.85rem', color: '#0c4a6e',
            }}>
              <i className="fas fa-info-circle me-2" />
              MySQL role levels are <strong>derived</strong> from the role's
              component privileges (recursive role-grant resolution) and update
              automatically when a privilege's level changes or it is certified.
            </div>
            <CertificationWorkspace
              databaseType="MYSQL"
              target="role"
              derived
              privilegeLevels={privilegeLevels}
              currentUser={currentUser}
              onDataChanged={() => {
                fetchCertificationStatus('MYSQL');
                fetchRoleStatistics();
              }}
            />
          </div>
        )}

        {/* Oracle Roles — DERIVED (read-only) Certification Workspace. Unlike
            MSSQL fixed roles, every Oracle role's level is computed from its
            component privileges (MAX; UNASSIGNED until every component is
            classified). Oracle roles are NOT independently assigned,
            suggested, or certified — so `derived` makes the workspace
            read-only (no Suggested column, no assign/certify/unassign, no
            bulk bar) and adds the maintained/user-defined filter. */}
        {selectedDatabaseType === 'ORACLE' && viewMode === 'roles' && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '25px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div style={{
              background: '#e7f1ff', border: '1px solid #b3d9ff',
              borderRadius: '8px', padding: '10px 14px', marginBottom: '14px',
              fontSize: '0.85rem', color: '#0c4a6e',
            }}>
              <i className="fas fa-info-circle me-2" />
              Oracle role levels are <strong>derived</strong> from the
              role's component privileges and update automatically when a
              privilege's level changes or it is certified. A role shows
              <strong> UNASSIGNED</strong> until every one of its component
              privileges has been classified — so role privilege levels may
              not be correct until all privileges are certified.
            </div>
            <CertificationWorkspace
              databaseType="ORACLE"
              target="role"
              derived
              privilegeLevels={privilegeLevels}
              currentUser={currentUser}
              onDataChanged={() => {
                fetchCertificationStatus('ORACLE');
                fetchRoleStatistics();
                // Keep the top-frame Recategorize Roles button in sync after
                // a role-tab change (same DB recategorization flag).
                checkRecategorizationStatus();
              }}
            />
          </div>
        )}

        {/* Cassandra Privileges — unified Certification Workspace. Cassandra
            has the same privilege ref shape as MSSQL/Oracle/MySQL
            (cassandra_privilege_mapping_reference with certified +
            suggested_level columns), so the generic workspace works as-is.
            Roles are read-only DERIVED (see Cassandra Roles block below). */}
        {selectedDatabaseType === 'CASSANDRA' && viewMode === 'privileges' && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '25px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CertificationWorkspace
              databaseType="CASSANDRA"
              target="privilege"
              privilegeLevels={privilegeLevels}
              currentUser={currentUser}
              onDataChanged={() => {
                fetchCertificationStatus('CASSANDRA');
                fetchStatistics();
              }}
            />
          </div>
        )}

        {/* Cassandra Roles — DERIVED (read-only) workspace, same model as
            Oracle/MySQL. cassandra_role_categorization has no certified column
            — every role's level is MAX over its grants (and inherited roles).
            `derived` mode hides the Suggested column + assign/certify/unassign
            actions + bulk bar. */}
        {selectedDatabaseType === 'CASSANDRA' && viewMode === 'roles' && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '25px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            {/* Accuracy warning — every Cassandra role level is MAX over its
                component privilege levels, so if ANY component privilege is
                still UNASSIGNED or NOT CERTIFIED the derived role level may
                be wrong: an unassigned privilege contributes 0 to the MAX
                (the role's "real" level could be higher once it's classified),
                and an uncertified one means the DBA hasn't validated the
                contributing level yet. Surface both counts with a deep-link
                so the DBA can go fix it. Hidden once everything is clean. */}
            {certificationStatus && (
              (certificationStatus.unassigned_count > 0 || certificationStatus.uncertified_count > 0)
            ) && (
              <div style={{
                background: '#fff3cd', border: '1px solid #ffe69c',
                borderRadius: '8px', padding: '10px 14px', marginBottom: '14px',
                fontSize: '0.85rem', color: '#664d03',
              }}>
                <i className="fas fa-exclamation-triangle me-2" />
                Role levels below may be inaccurate.
                {certificationStatus.unassigned_count > 0 && (
                  <> <strong>{certificationStatus.unassigned_count}</strong> privilege{certificationStatus.unassigned_count === 1 ? ' is' : 's are'} <strong>UNASSIGNED</strong> (contributes 0 to the role's MAX — the true level may be higher).</>
                )}
                {certificationStatus.uncertified_count > 0 && (
                  <> <strong>{certificationStatus.uncertified_count}</strong> privilege{certificationStatus.uncertified_count === 1 ? ' is' : 's are'} <strong>not yet certified</strong> (the contributing level has not been DBA-validated).</>
                )}
                {' '}
                <button
                  type="button"
                  className="btn btn-link p-0 align-baseline"
                  style={{ fontSize: '0.85rem', color: '#664d03', textDecoration: 'underline' }}
                  onClick={() => setViewMode('privileges')}
                >
                  Open the Privileges tab to resolve
                </button>.
              </div>
            )}
            <div style={{
              background: '#e7f1ff', border: '1px solid #b3d9ff',
              borderRadius: '8px', padding: '10px 14px', marginBottom: '14px',
              fontSize: '0.85rem', color: '#0c4a6e',
            }}>
              <i className="fas fa-info-circle me-2" />
              Cassandra role levels are <strong>derived</strong> from the
              role's grants (resource permissions and inherited parent roles)
              and update automatically when a privilege's level changes or it
              is certified.
            </div>
            <CertificationWorkspace
              databaseType="CASSANDRA"
              target="role"
              derived
              privilegeLevels={privilegeLevels}
              currentUser={currentUser}
              onDataChanged={() => {
                fetchCertificationStatus('CASSANDRA');
                fetchRoleStatistics();
              }}
            />
          </div>
        )}

        {/* Statistics Dashboard - Roles */}
        {!usesWorkspace && selectedDatabaseType && viewMode === 'roles' && roleStatistics && (
          <>
            {selectedDatabaseType === 'MSSQL' ? (
              <>
                {/* MSSQL: First row - Role type statistics */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(5, 1fr)',
                  gap: '10px',
                  marginBottom: '10px'
                }}>
                  <StatCard
                    title="Total Roles"
                    value={roleStatistics.total_roles}
                    color="#667eea"
                  />
                  <StatCard
                    title="Fixed Server"
                    value={roles.filter(r => r.is_mssql_maintained && r.is_server_role).length}
                    color="#2563eb"
                  />
                  <StatCard
                    title="Fixed Database"
                    value={roles.filter(r => r.is_mssql_maintained && !r.is_server_role).length}
                    color="#0ea5e9"
                  />
                  <StatCard
                    title="User Server"
                    value={roles.filter(r => !r.is_mssql_maintained && r.is_server_role).length}
                    color="#059669"
                  />
                  <StatCard
                    title="User Database"
                    value={roles.filter(r => !r.is_mssql_maintained && !r.is_server_role).length}
                    color="#10b981"
                  />
                </div>
                {/* MSSQL: Second row - Privilege level statistics */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                  gap: '10px',
                  marginBottom: '20px'
                }}>
                  {Object.entries(roleStatistics.by_level || {}).map(([level, count]) => {
                    // Defense-in-depth: a null/empty/'null' key (legacy rows
                    // with no level name) must never render a literal "null"
                    // card — show it as UNASSIGNED instead. The backend now
                    // coalesces these, so this is a safety net.
                    const label = (!level || level === 'null') ? 'UNASSIGNED' : level;
                    const levelColor = getLevelColor(label).color;
                    return (
                      <StatCard
                        key={label}
                        title={label}
                        value={count}
                        color={levelColor}
                      />
                    );
                  })}
                </div>
              </>
            ) : (
              /* Other databases: Single row */
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: '12px',
                marginBottom: '20px'
              }}>
                <StatCard
                  title="Total Roles"
                  value={roleStatistics.total_roles}
                  color="#667eea"
                />
                {Object.entries(roleStatistics.by_level || {}).map(([level, count]) => {
                  const levelColor = getLevelColor(level).color;
                  return (
                    <StatCard
                      key={level}
                      title={level}
                      value={count}
                      color={levelColor}
                    />
                  );
                })}
                {selectedDatabaseType === 'ORACLE' && roleStatistics.oracle_maintained !== undefined && (
                  <>
                    <StatCard
                      title="Oracle Maintained"
                      value={roleStatistics.oracle_maintained}
                      color="#0dcaf0"
                    />
                    <StatCard
                      title="Custom Roles"
                      value={roleStatistics.custom_roles}
                      color="#f093fb"
                    />
                  </>
                )}
                {selectedDatabaseType === 'POSTGRESQL' && roleStatistics.system_roles !== undefined && (
                  <>
                    <StatCard
                      title="System Roles"
                      value={roleStatistics.system_roles}
                      color="#0dcaf0"
                    />
                    <StatCard
                      title="Custom Roles"
                      value={roleStatistics.custom_roles}
                      color="#f093fb"
                    />
                  </>
                )}
              </div>
            )}
          </>
        )}

        {/* Recategorization Message */}
        {recategorizationMessage && (
          <div style={{
            background: recategorizationMessage.startsWith('Success') ? '#d4edda' : '#f8d7da',
            border: `2px solid ${recategorizationMessage.startsWith('Success') ? '#28a745' : '#dc3545'}`,
            borderRadius: '12px',
            padding: '15px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <p style={{
              color: recategorizationMessage.startsWith('Success') ? '#155724' : '#721c24',
              marginBottom: '0',
              fontSize: '0.95rem',
              fontWeight: '500'
            }}>
              {recategorizationMessage}
            </p>
          </div>
        )}

        {/* Recategorize Roles confirmation modal (replaces window.confirm).
            Oracle role level = MAX of its component privilege levels, and the
            recalculator only trusts an ASSIGNED + CERTIFIED privilege level.
            If any privilege is UNASSIGNED or uncertified the derived role
            tiers would be wrong, so we BLOCK the run and tell the DBA exactly
            what to fix first. Counts come from the same certificationStatus
            payload that drives the campaign-blocking banner. */}
        {showRecategorizationModal && (() => {
          const unassigned = certificationStatus?.unassigned_count || 0;
          const uncertified = certificationStatus?.uncertified_count || 0;
          const roleUncert = certificationStatus?.role_uncertified_count || 0;
          const blocked = unassigned > 0 || uncertified > 0 || roleUncert > 0;
          return (
            <WideModal
              show={showRecategorizationModal}
              onClose={() => setShowRecategorizationModal(false)}
              title="Recategorize Oracle Roles"
              headerIcon="fa-sync-alt"
              headerClass={blocked ? 'bg-danger text-white' : 'bg-primary text-white'}
              maxWidth="720px"
              footer={
                <>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowRecategorizationModal(false)}
                  >
                    {blocked ? 'Close' : 'Cancel'}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={confirmRunRecategorization}
                    disabled={blocked}
                    title={blocked
                      ? 'Resolve the unassigned/uncertified privileges first'
                      : 'Run role recategorization now'}
                  >
                    <i className="fas fa-sync-alt me-2"></i>
                    Recategorize Roles
                  </button>
                </>
              }
            >
              {blocked ? (
                <div>
                  <div className="alert alert-danger" role="alert" style={{ marginBottom: '16px' }}>
                    <strong>
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      Recategorization is blocked — it would produce inaccurate role levels.
                    </strong>
                  </div>
                  <p style={{ fontSize: '0.95rem' }}>
                    Oracle role privilege levels are <strong>derived</strong> from
                    their component privileges (a role's level is the maximum of
                    the privileges it grants). The categorizer only trusts a
                    privilege level that has been <strong>assigned</strong> and
                    <strong> certified</strong>. While the following remain
                    outstanding, the recomputed role tiers would be wrong:
                  </p>
                  <ul style={{ fontSize: '0.95rem', lineHeight: 1.7 }}>
                    {unassigned > 0 && (
                      <li>
                        <strong>{unassigned}</strong> unassigned privilege{unassigned === 1 ? '' : 's'}
                        {' '}— assign a privilege level on the <strong>Privileges</strong> tab.
                      </li>
                    )}
                    {uncertified > 0 && (
                      <li>
                        <strong>{uncertified}</strong> uncertified privilege{uncertified === 1 ? '' : 's'}
                        {' '}— certify on the <strong>Privileges</strong> tab.
                      </li>
                    )}
                    {roleUncert > 0 && (
                      <li>
                        <strong>{roleUncert}</strong> uncertified role{roleUncert === 1 ? '' : 's'}
                        {' '}— review on the <strong>Roles</strong> tab.
                      </li>
                    )}
                  </ul>
                  <p style={{ fontSize: '0.9rem', color: '#6c757d', marginBottom: 0 }}>
                    Resolve every item above, then return here to recategorize.
                  </p>
                </div>
              ) : (
                <div>
                  <p style={{ fontSize: '0.95rem' }}>
                    All {certificationStatus?.total_count != null ? <strong>{certificationStatus.total_count}</strong> : ''} Oracle
                    privileges are assigned and certified. Recategorization will
                    recompute every Oracle role's privilege level from its
                    component privileges (role level = maximum of its granted
                    privilege levels) and cascade the new levels to affected users.
                  </p>
                  <p style={{ fontSize: '0.95rem', marginBottom: 0 }}>
                    {recategorizationStatus?.change_description
                      ? <>Reason: <em>{recategorizationStatus.change_description}</em></>
                      : 'This is safe to run now. Continue?'}
                  </p>
                </div>
              )}
            </WideModal>
          );
        })()}

        {/* Main Content - Privileges */}
        {!usesWorkspace && selectedDatabaseType && viewMode === 'privileges' && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '25px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            {/* (Legacy non-workspace path. The Oracle "Recategorize Roles"
                button was moved to the page top frame; Oracle uses the
                CertificationWorkspace path above, not this block.) */}

            {/* Filters and Sorting */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{
                display: 'grid',
                // 5 filters + the Clear button on one row. Fixed fractional
                // columns (not auto-fit) so they always stay on a single line;
                // the trailing column is sized to the button.
                gridTemplateColumns: '1.4fr 1fr 1fr 1fr 1fr auto',
                gap: '10px',
                alignItems: 'end',
                marginBottom: '15px'
              }}>
                {/* Search */}
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.8rem', fontWeight: '600' }}>
                    Search Privilege Name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search privileges..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    style={{ fontSize: '0.82rem' }}
                  />
                </div>

                {/* Privilege Type Filter */}
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.8rem', fontWeight: '600' }}>
                    Privilege Type
                  </label>
                  <select
                    className="form-select"
                    value={filters.privilege_type}
                    onChange={(e) => handleFilterChange('privilege_type', e.target.value)}
                    style={{ fontSize: '0.82rem' }}
                  >
                    <option value="">All Types</option>
                    {getPrivilegeTypesForCurrentDatabase().map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                {/* Privilege Level Filter */}
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.8rem', fontWeight: '600' }}>
                    Privilege Level
                  </label>
                  <select
                    className="form-select"
                    value={filters.privilege_level_name}
                    onChange={(e) => handleFilterChange('privilege_level_name', e.target.value)}
                    style={{ fontSize: '0.82rem' }}
                  >
                    <option value="">All Levels</option>
                    {/* UNASSIGNED is the -1 sentinel (discovered, no DBA-assigned
                        level). It is not a real tier so it's absent from
                        privilegeLevels; add it explicitly so a DBA can filter to
                        exactly the rows that still need a level before certification. */}
                    <option value="UNASSIGNED">UNASSIGNED</option>
                    {privilegeLevels.map(level => (
                      <option key={level.value} value={level.name}>{level.name}</option>
                    ))}
                  </select>
                </div>

                {/* Suggested Level Filter — doc-derived suggestion for
                    UNASSIGNED items, already clamped to the privilege matrix's
                    enabled tiers (so privilegeLevels is the correct option set). */}
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.8rem', fontWeight: '600' }}>
                    Suggested Level
                  </label>
                  <select
                    className="form-select"
                    value={filters.suggested_level_name}
                    onChange={(e) => handleFilterChange('suggested_level_name', e.target.value)}
                    style={{ fontSize: '0.82rem' }}
                  >
                    <option value="">All Suggestions</option>
                    {privilegeLevels.map(level => (
                      <option key={level.value} value={level.name}>{level.name}</option>
                    ))}
                  </select>
                </div>

                {/* Certification Status Filter */}
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.8rem', fontWeight: '600' }}>
                    Certification Status
                  </label>
                  <select
                    className="form-select"
                    value={filters.certified}
                    onChange={(e) => handleFilterChange('certified', e.target.value)}
                    style={{ fontSize: '0.82rem' }}
                  >
                    <option value="">All</option>
                    <option value="true">Certified</option>
                    <option value="false">Uncertified</option>
                  </select>
                </div>

                {/* Clear Filters — sits in the grid's trailing column so it
                    stays on the same row as the filters. Always rendered (so
                    the column width is stable); disabled when no filter set. */}
                <div>
                  <button
                    className="btn btn-outline-secondary"
                    onClick={handleClearAllFilters}
                    disabled={!(filters.search || filters.privilege_type || filters.privilege_level_name || filters.suggested_level_name || filters.certified)}
                    style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}
                    title="Clear all filters"
                  >
                    <i className="fas fa-times-circle" style={{ marginRight: '6px' }}></i>
                    Clear
                  </button>
                </div>
              </div>

              {/* Bulk Actions */}
              {selectedItems.length > 0 && (
                <div style={{
                  padding: '15px',
                  background: '#e7f3ff',
                  borderRadius: '8px',
                  border: '1px solid #b3d9ff'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: '600', color: '#004085' }}>
                      <i className="fas fa-check-square" style={{ marginRight: '8px' }}></i>
                      {selectedItems.length} selected
                    </span>
                    <select
                      className="form-select"
                      value={bulkCertifyAction}
                      onChange={(e) => setBulkCertifyAction(e.target.value)}
                      style={{ width: '200px', fontSize: '0.9rem' }}
                    >
                      <option value="">Select Action...</option>
                      <option value="certify">Certify Selected</option>
                      <option value="uncertify">Uncertify Selected</option>
                    </select>
                    <button
                      className="btn btn-primary"
                      onClick={handleBulkUpdateCertification}
                      disabled={!bulkCertifyAction}
                      style={{ fontSize: '0.9rem' }}
                    >
                      <i className="fas fa-check-circle" style={{ marginRight: '8px' }}></i>
                      Apply
                    </button>
                    <div style={{ borderLeft: '2px solid #b3d9ff', height: '30px' }}></div>
                    <select
                      className="form-select"
                      value={bulkLevelChange}
                      onChange={(e) => setBulkLevelChange(e.target.value)}
                      style={{ width: '220px', fontSize: '0.9rem' }}
                    >
                      <option value="">Change Level...</option>
                      {privilegeLevels.map(level => (
                        <option key={level.value} value={level.value}>
                          {level.name}
                        </option>
                      ))}
                    </select>
                    <button
                      className="btn btn-success"
                      onClick={handleBulkUpdateLevel}
                      disabled={!bulkLevelChange}
                      style={{ fontSize: '0.9rem' }}
                    >
                      <i className="fas fa-level-up-alt" style={{ marginRight: '8px' }}></i>
                      Apply Level
                    </button>
                    <div style={{ borderLeft: '2px solid #b3d9ff', height: '30px' }}></div>
                    <button
                      className="btn btn-info text-white"
                      onClick={openApplySuggestedModal}
                      disabled={suggestableSelected.length === 0}
                      title={suggestableSelected.length === 0
                        ? 'Select UNASSIGNED items that have a suggested level'
                        : `Apply documentation-suggested levels to ${suggestableSelected.length} selected item(s)`}
                      style={{ fontSize: '0.9rem' }}
                    >
                      <i className="fas fa-lightbulb" style={{ marginRight: '8px' }}></i>
                      Apply Suggested ({suggestableSelected.length})
                    </button>
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => setSelectedItems([])}
                      style={{ fontSize: '0.9rem' }}
                    >
                      Clear Selection
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Table */}
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', marginBottom: '10px' }}></i>
                <div>Loading privileges...</div>
              </div>
            ) : (
              <>
                {privileges.length > 0 ? (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="table table-hover" style={{ fontSize: '0.9rem' }}>
                      <thead style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                        <tr>
                          <th style={{ width: '40px' }}>
                            <input
                              type="checkbox"
                              checked={selectedItems.length === privileges.length && privileges.length > 0}
                              onChange={handleSelectAll}
                            />
                          </th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSortChange('privilege_name')}>
                            Privilege Name {getSortIcon('privilege_name')}
                          </th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSortChange('privilege_type')}>
                            Type {getSortIcon('privilege_type')}
                          </th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSortChange('privilege_level')}>
                            Level {getSortIcon('privilege_level')}
                          </th>
                          <th title="Suggested from technology documentation, clamped to the privilege matrix. A hint only — the DBA decides.">
                            Suggested
                          </th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSortChange('certified')}>
                            Certified {getSortIcon('certified')}
                          </th>
                          <th>Certified By</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {privileges.map(priv => (
                          <tr key={priv.id}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedItems.includes(priv.id)}
                                onChange={() => handleSelectItem(priv.id)}
                              />
                            </td>
                            <td style={{ fontWeight: '600', color: '#1a1a2e' }}>{priv.privilege_name}</td>
                            <td>
                              <span className={priv.privilege_type === 'SYSTEM' ? 'text-primary' : 'text-success'}>
                                {priv.privilege_type}
                              </span>
                            </td>
                            <td>
                              <PrivilegeLevelBadge levelName={priv.privilege_level_name} />
                            </td>
                            <td>
                              {priv.suggested_level_name ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <PrivilegeLevelBadge levelName={priv.suggested_level_name} />
                                  {priv.privilege_level_name === 'UNASSIGNED' && (
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      style={{ padding: '0 6px', fontSize: '0.7rem' }}
                                      title={`Open the level editor pre-filled with the suggested level (${priv.suggested_level_name}). You still confirm the assignment.`}
                                      onClick={() => {
                                        setEditingPrivilege(priv);
                                        setEditForm({
                                          privilege_level: priv.suggested_level,
                                          privilege_level_name: priv.suggested_level_name,
                                          notes: priv.notes || ''
                                        });
                                        setShowEditModal(true);
                                      }}
                                    >
                                      Apply
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted" style={{ fontStyle: 'italic', fontSize: '0.85rem' }}>—</span>
                              )}
                            </td>
                            <td>
                              {priv.certified ? (
                                <span className="text-success">
                                  <i className="fas fa-check-circle me-1"></i>Yes
                                </span>
                              ) : (
                                <span className="text-warning">
                                  <i className="fas fa-clock me-1"></i>No
                                </span>
                              )}
                            </td>
                            <td>
                              {priv.certified_by ? (
                                <small style={{ color: '#6c757d' }}>
                                  {priv.certified_by}
                                  {priv.certified_date && (
                                    <div style={{ fontSize: '0.75rem' }}>
                                      {new Date(priv.certified_date).toLocaleDateString()}
                                    </div>
                                  )}
                                </small>
                              ) : (
                                <span style={{ color: '#6c757d', fontStyle: 'italic' }}>-</span>
                              )}
                            </td>
                            <td>
                              <div style={{ display: 'flex', gap: '5px' }}>
                                {priv.certified ? (
                                  <button
                                    className="btn btn-sm btn-outline-warning"
                                    onClick={() => handleUpdateCertification(priv.id, false)}
                                    title="Uncertify"
                                  >
                                    <i className="fas fa-times-circle"></i>
                                  </button>
                                ) : (
                                  <button
                                    className="btn btn-sm btn-outline-success"
                                    onClick={() => handleUpdateCertification(priv.id, true)}
                                    title="Certify"
                                  >
                                    <i className="fas fa-check-circle"></i>
                                  </button>
                                )}
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => handleOpenEditModal(priv)}
                                  title="Edit Level"
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                    <i className="fas fa-inbox" style={{ fontSize: '3rem', marginBottom: '15px', opacity: 0.3 }}></i>
                    <div style={{ fontSize: '1.1rem' }}>No privileges found</div>
                    <div style={{ fontSize: '0.9rem', marginTop: '5px' }}>
                      Try adjusting your filters
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}


        {/* Main Content - Roles */}
        {!usesWorkspace && selectedDatabaseType && viewMode === 'roles' && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '25px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            {/* Filters and Sorting - Simplified for Roles */}
            <div style={{ marginBottom: '20px' }}>
              {/* Single-line filter row: fixed fractional columns so all
                  filters + Clear stay on one line (matches the privileges tab). */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: selectedDatabaseType === 'MSSQL'
                  ? '1.4fr 1fr 1fr 1.2fr 1fr auto'
                  : '1.6fr 1fr 1fr 1fr auto',
                gap: '10px',
                alignItems: 'end',
                marginBottom: '15px'
              }}>
                {/* Search */}
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.8rem', fontWeight: '600' }}>
                    Search Role Name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search roles..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    style={{ fontSize: '0.82rem' }}
                  />
                </div>

                {/* Privilege Level Filter */}
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.8rem', fontWeight: '600' }}>
                    Privilege Level
                  </label>
                  <select
                    className="form-select"
                    value={filters.privilege_level_name}
                    onChange={(e) => handleFilterChange('privilege_level_name', e.target.value)}
                    style={{ fontSize: '0.82rem' }}
                  >
                    <option value="">All Levels</option>
                    <option value="UNASSIGNED">UNASSIGNED</option>
                    {privilegeLevels.map(level => (
                      <option key={level.value} value={level.name}>{level.name}</option>
                    ))}
                  </select>
                </div>

                {/* Suggested Level Filter */}
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.8rem', fontWeight: '600' }}>
                    Suggested Level
                  </label>
                  <select
                    className="form-select"
                    value={filters.suggested_level_name}
                    onChange={(e) => handleFilterChange('suggested_level_name', e.target.value)}
                    style={{ fontSize: '0.82rem' }}
                  >
                    <option value="">All Suggestions</option>
                    {privilegeLevels.map(level => (
                      <option key={level.value} value={level.name}>{level.name}</option>
                    ))}
                  </select>
                </div>

                {/* Certification Status — MSSQL only */}
                {selectedDatabaseType === 'MSSQL' && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.8rem', fontWeight: '600' }}>
                      <i className="fas fa-certificate" style={{ marginRight: '4px', color: '#0ea5e9' }}></i>
                      Certification
                    </label>
                    <select
                      className="form-select"
                      value={filters.certification_status}
                      onChange={(e) => handleFilterChange('certification_status', e.target.value)}
                      style={{ fontSize: '0.82rem' }}
                    >
                      <option value="">All Statuses</option>
                      <option value="derived_certified">Derived - Certified</option>
                      <option value="derived_pending">Derived - Pending</option>
                      <option value="manually_certified">Manually Certified</option>
                      <option value="pending">Pending Certification</option>
                    </select>
                  </div>
                )}

                {/* As On Date */}
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.8rem', fontWeight: '600' }}>
                    As On Date
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={asOnDate}
                    onChange={(e) => setAsOnDate(e.target.value)}
                    style={{ fontSize: '0.82rem' }}
                    title="View roles as of this date (default: today)"
                  />
                </div>

                {/* Clear — trailing grid column, always rendered for stable width */}
                <div>
                  <button
                    className="btn btn-outline-secondary"
                    onClick={handleClearAllFilters}
                    disabled={!(filters.search || filters.privilege_level_name || filters.suggested_level_name || filters.certification_status)}
                    style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}
                    title="Clear all filters"
                  >
                    <i className="fas fa-times-circle" style={{ marginRight: '6px' }}></i>
                    Clear
                  </button>
                </div>
              </div>

              {/* Oracle role-type checkboxes — compact inline strip */}
              {selectedDatabaseType === 'ORACLE' && (
                <div style={{ display: 'flex', gap: '20px', alignItems: 'center', marginBottom: '12px', fontSize: '0.85rem' }}>
                  <span style={{ fontWeight: 600 }}>Role Type:</span>
                  <div className="form-check mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="oracleMaintainedFilter"
                      checked={roleTypeFilters.oracle_maintained}
                      onChange={(e) => setRoleTypeFilters(prev => ({ ...prev, oracle_maintained: e.target.checked }))}
                    />
                    <label className="form-check-label" htmlFor="oracleMaintainedFilter">
                      Oracle Maintained
                    </label>
                  </div>
                  <div className="form-check mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="userDefinedFilter"
                      checked={roleTypeFilters.user_defined}
                      onChange={(e) => setRoleTypeFilters(prev => ({ ...prev, user_defined: e.target.checked }))}
                    />
                    <label className="form-check-label" htmlFor="userDefinedFilter">
                      User Defined
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* MSSQL Role View Mode Toggle */}
            {selectedDatabaseType === 'MSSQL' && (
              <div style={{
                background: '#f8fafc',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '15px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ fontWeight: '600', marginBottom: '10px', color: '#334155' }}>
                  <i className="fas fa-layer-group" style={{ marginRight: '8px', color: '#6366f1' }}></i>
                  Role Category
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    className={`btn btn-sm ${mssqlRoleViewMode === 'fixed' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => { setMssqlRoleViewMode('fixed'); setSelectedRoles([]); }}
                    style={{ flex: 1 }}
                  >
                    <i className="fas fa-lock" style={{ marginRight: '6px' }}></i>
                    Fixed Roles ({roles.filter(r => r.is_fixed_role).length})
                  </button>
                  <button
                    className={`btn btn-sm ${mssqlRoleViewMode === 'user_defined' ? 'btn-warning' : 'btn-outline-warning'}`}
                    onClick={() => { setMssqlRoleViewMode('user_defined'); setSelectedRoles([]); }}
                    style={{ flex: 1 }}
                  >
                    <i className="fas fa-user-edit" style={{ marginRight: '6px' }}></i>
                    User-Defined Roles ({roles.filter(r => !r.is_fixed_role).length})
                  </button>
                  <button
                    className={`btn btn-sm ${mssqlRoleViewMode === 'all' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                    onClick={() => { setMssqlRoleViewMode('all'); setSelectedRoles([]); }}
                    style={{ flex: 1 }}
                  >
                    <i className="fas fa-list" style={{ marginRight: '6px' }}></i>
                    All Roles ({roles.length})
                  </button>
                </div>
                <div style={{ marginTop: '10px', fontSize: '0.85rem', color: '#64748b' }}>
                  {mssqlRoleViewMode === 'fixed' && (
                    <span><i className="fas fa-info-circle" style={{ marginRight: '6px' }}></i>Fixed roles are SQL Server built-in roles. You can update their privilege level and certify them.</span>
                  )}
                  {mssqlRoleViewMode === 'user_defined' && (
                    <span><i className="fas fa-info-circle" style={{ marginRight: '6px' }}></i>User-defined roles have their privilege level automatically calculated from underlying permissions. They cannot be manually updated.</span>
                  )}
                  {mssqlRoleViewMode === 'all' && (
                    <span><i className="fas fa-info-circle" style={{ marginRight: '6px' }}></i>Showing all roles. Only fixed roles can be manually updated.</span>
                  )}
                </div>
              </div>
            )}

            {/* Bulk Actions Bar - MSSQL Only */}
            {selectedDatabaseType === 'MSSQL' && selectedRoles.length > 0 && (
              <div style={{
                background: '#e0f2fe',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '15px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                border: '1px solid #0ea5e9'
              }}>
                <div style={{ fontWeight: '600', color: '#0c4a6e' }}>
                  <i className="fas fa-check-circle" style={{ marginRight: '8px', color: '#0ea5e9' }}></i>
                  <strong>{selectedRoles.length}</strong> role(s) selected
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    className="btn btn-success btn-sm"
                    onClick={handleBulkCertifyRoles}
                    title="Certify all selected roles"
                  >
                    <i className="fas fa-certificate" style={{ marginRight: '6px' }}></i>
                    Certify Selected
                  </button>
                  {(() => {
                    const suggestableRoles = roles.filter(
                      r => selectedRoles.includes(r.id)
                        && r.privilege_level_name === 'UNASSIGNED'
                        && r.suggested_level_name
                    );
                    return (
                      <button
                        className="btn btn-info btn-sm text-white"
                        onClick={() => handleApplySuggestedRoleLevels(suggestableRoles)}
                        disabled={suggestableRoles.length === 0}
                        title={suggestableRoles.length === 0
                          ? 'Select UNASSIGNED roles that have a suggested level'
                          : `Apply documentation-suggested levels to ${suggestableRoles.length} selected role(s)`}
                      >
                        <i className="fas fa-lightbulb" style={{ marginRight: '6px' }}></i>
                        Apply Suggested ({suggestableRoles.length})
                      </button>
                    );
                  })()}
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleBulkChangeRoleLevel}
                    title="Change privilege level for selected roles"
                  >
                    <i className="fas fa-edit" style={{ marginRight: '6px' }}></i>
                    Change Level
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setSelectedRoles([])}
                    title="Clear selection"
                  >
                    <i className="fas fa-times" style={{ marginRight: '6px' }}></i>
                    Clear Selection
                  </button>
                </div>
              </div>
            )}

            {/* Table */}
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', marginBottom: '10px' }}></i>
                <div>Loading roles...</div>
              </div>
            ) : (
              <>
                {(() => {
                  // Filter roles based on role type filters
                  const filteredRoles = roles.filter(role => {
                    if (selectedDatabaseType === 'ORACLE') {
                      if (role.is_oracle_maintained && !roleTypeFilters.oracle_maintained) {
                        return false;
                      }
                      if (!role.is_oracle_maintained && !roleTypeFilters.user_defined) {
                        return false;
                      }
                    }
                    if (selectedDatabaseType === 'MSSQL') {
                      // Apply MSSQL role view mode filter (fixed vs user-defined)
                      if (mssqlRoleViewMode === 'fixed' && !role.is_fixed_role) return false;
                      if (mssqlRoleViewMode === 'user_defined' && role.is_fixed_role) return false;

                      // Apply certification status filter
                      if (filters.certification_status) {
                        const isUserDefined = !role.is_fixed_role;
                        const isFixedRole = role.is_fixed_role;

                        if (filters.certification_status === 'derived_certified') {
                          // User-defined roles that are certified
                          if (!isUserDefined || !role.certified) return false;
                        } else if (filters.certification_status === 'derived_pending') {
                          // User-defined roles that are not certified
                          if (!isUserDefined || role.certified) return false;
                        } else if (filters.certification_status === 'manually_certified') {
                          // Fixed roles that are certified
                          if (!isFixedRole || !role.certified) return false;
                        } else if (filters.certification_status === 'pending') {
                          // Fixed roles that are not certified
                          if (!isFixedRole || role.certified) return false;
                        }
                      }
                    }
                    return true;
                  });

                  return filteredRoles.length > 0 ? (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="table table-hover" style={{ fontSize: '0.9rem' }}>
                      <thead style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                        <tr>
                          {selectedDatabaseType === 'MSSQL' && (
                            <th style={{ width: '40px', textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                onChange={(e) => {
                                  // Predefined roles only (user-defined roles
                                  // auto-certify from privileges). UNASSIGNED
                                  // roles ARE selectable — selecting them is how
                                  // a DBA assigns a level / applies suggestions;
                                  // the certify action separately rejects
                                  // still-UNASSIGNED rows.
                                  const selectableRoles = filteredRoles.filter(role =>
                                    role.is_fixed_role || role.is_mssql_maintained || role.is_oracle_maintained);
                                  if (e.target.checked) {
                                    setSelectedRoles(selectableRoles.map(r => r.id));
                                  } else {
                                    setSelectedRoles([]);
                                  }
                                }}
                                checked={selectedRoles.length > 0 && selectedRoles.length === filteredRoles.filter(role => role.is_fixed_role || role.is_mssql_maintained || role.is_oracle_maintained).length}
                                title="Select all predefined roles (assign level / apply suggested / certify)"
                              />
                            </th>
                          )}
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSortChange('role_name')}>
                            Role Name {getSortIcon('role_name')}
                          </th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSortChange('privilege_level')}>
                            Level {getSortIcon('privilege_level')}
                          </th>
                          <th title="Suggested from technology documentation, clamped to the privilege matrix. A hint only — the DBA decides.">
                            Suggested
                          </th>
                          {selectedDatabaseType === 'ORACLE' && (
                            <>
                              <th>Oracle Maintained</th>
                              <th>Database Name</th>
                              <th>Direct System Privs</th>
                              <th>Direct Table Privs</th>
                              <th>Granted Roles</th>
                            </>
                          )}
                          {selectedDatabaseType === 'MSSQL' && (
                            <>
                              <th>Type</th>
                              <th>Fixed/Custom</th>
                              <th>Certification Status</th>
                              <th>Direct Server Privs</th>
                              <th>Direct Database Privs</th>
                              <th>Direct Table Privs</th>
                            </>
                          )}
                          {selectedDatabaseType === 'POSTGRESQL' && (
                            <>
                              <th>System Role</th>
                              <th>Database Name</th>
                              <th>Direct Global Privs</th>
                              <th>Direct Table Privs</th>
                              <th>Granted Roles</th>
                            </>
                          )}
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSortChange('categorized_date')}>
                            Categorized Date {getSortIcon('categorized_date')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRoles.map((role, index) => (
                          <tr key={role.id || index}>
                            {selectedDatabaseType === 'MSSQL' && (
                              <td style={{ textAlign: 'center', width: '40px' }}>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={selectedRoles.includes(role.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedRoles([...selectedRoles, role.id]);
                                    } else {
                                      setSelectedRoles(selectedRoles.filter(id => id !== role.id));
                                    }
                                  }}
                                  disabled={
                                    !role.is_fixed_role && !role.is_oracle_maintained && !role.is_mssql_maintained
                                  }
                                  title={
                                    (!role.is_fixed_role && !role.is_oracle_maintained && !role.is_mssql_maintained)
                                      ? "User-defined roles are automatically certified based on underlying privileges"
                                      : "Select role for bulk operations (assign level / apply suggested / certify)"
                                  }
                                  style={{ cursor: (
                                    !role.is_fixed_role && !role.is_oracle_maintained && !role.is_mssql_maintained
                                  ) ? 'not-allowed' : 'pointer' }}
                                />
                              </td>
                            )}
                            <td style={{ fontWeight: '600' }}>
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleShowRoleDetails(
                                    role.role_name,
                                    role.database_name || null,
                                    role.dbid || null
                                  );
                                }}
                                style={{
                                  color: '#667eea',
                                  textDecoration: 'none',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  transition: 'color 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                title="Click to view role privileges"
                              >
                                <i className="fas fa-info-circle" style={{ marginRight: '6px', fontSize: '0.9em' }}></i>
                                {role.role_name}
                              </a>
                            </td>
                            <td>
                              <PrivilegeLevelBadge levelName={role.privilege_level_name} />
                            </td>
                            <td>
                              {role.suggested_level_name ? (
                                <span
                                  className="text-info"
                                  title="Documentation-derived suggestion (matrix-clamped). The DBA decides."
                                >
                                  <i className="fas fa-lightbulb me-1"></i>
                                  {role.suggested_level_name}
                                </span>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            {selectedDatabaseType === 'ORACLE' && (
                              <>
                                <td>
                                  {role.is_oracle_maintained ? (
                                    <span className="text-info">
                                      <i className="fas fa-check me-1"></i>Yes
                                    </span>
                                  ) : (
                                    <span className="text-secondary">No</span>
                                  )}
                                </td>
                                <td>
                                  {!role.is_oracle_maintained && role.database_name ? (
                                    <small style={{ color: '#1a1a2e', fontWeight: '500' }}>
                                      {role.database_name}
                                    </small>
                                  ) : (
                                    <span style={{ color: '#6c757d' }}>-</span>
                                  )}
                                </td>
                                <td>
                                  {role.direct_system_privs ? (
                                    <a
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleShowPrivilegeDetails(role.role_name, 'system', role.direct_system_privs);
                                      }}
                                      style={{
                                        color: '#667eea',
                                        textDecoration: 'none',
                                        fontSize: '0.85rem',
                                        fontWeight: '500'
                                      }}
                                    >
                                      <i className="fas fa-list-ul" style={{ marginRight: '5px' }}></i>
                                      {role.direct_system_privs.split(',').length} privileges
                                    </a>
                                  ) : (
                                    <span style={{ color: '#6c757d' }}>-</span>
                                  )}
                                </td>
                                <td>
                                  {role.direct_table_privs ? (
                                    <a
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleShowPrivilegeDetails(role.role_name, 'table', role.direct_table_privs);
                                      }}
                                      style={{
                                        color: '#667eea',
                                        textDecoration: 'none',
                                        fontSize: '0.85rem',
                                        fontWeight: '500'
                                      }}
                                    >
                                      <i className="fas fa-table" style={{ marginRight: '5px' }}></i>
                                      {role.direct_table_privs.split(',').length} privileges
                                    </a>
                                  ) : (
                                    <span style={{ color: '#6c757d' }}>-</span>
                                  )}
                                </td>
                                <td>
                                  {role.granted_roles ? (
                                    <a
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleShowPrivilegeDetails(role.role_name, 'roles', role.granted_roles, role.database_name, role.dbid);
                                      }}
                                      style={{
                                        color: '#667eea',
                                        textDecoration: 'none',
                                        fontSize: '0.85rem',
                                        fontWeight: '500'
                                      }}
                                    >
                                      <i className="fas fa-users-cog" style={{ marginRight: '5px' }}></i>
                                      {role.granted_roles.split(',').length} roles
                                    </a>
                                  ) : (
                                    <span style={{ color: '#6c757d' }}>-</span>
                                  )}
                                </td>
                              </>
                            )}
                            {selectedDatabaseType === 'MSSQL' && (
                              <>
                                {/* Role Type Column */}
                                <td>
                                  {role.is_server_role ? (
                                    <span className="text-primary">
                                      <i className="fas fa-server me-1"></i>Server
                                    </span>
                                  ) : (
                                    <span className="text-info">
                                      <i className="fas fa-database me-1"></i>Database
                                    </span>
                                  )}
                                </td>

                                {/* Fixed/Custom Column */}
                                <td>
                                  {role.is_fixed_role ? (
                                    <span className="text-info">
                                      <i className="fas fa-lock me-1"></i>Fixed
                                    </span>
                                  ) : (
                                    <span className="text-success">
                                      <i className="fas fa-user-cog me-1"></i>Custom
                                    </span>
                                  )}
                                </td>

                                {/* Certification Status Column */}
                                <td>
                                  {!role.is_fixed_role ? (
                                    // User-defined roles (derived certification)
                                    role.certified ? (
                                      <div>
                                        <span className="text-success" title="All underlying privileges are certified">
                                          <i className="fas fa-link me-1"></i>Derived ✓
                                        </span>
                                        <div className="small text-muted mt-1">
                                          Auto-certified
                                        </div>
                                      </div>
                                    ) : (
                                      <span className="text-warning" title="Has uncertified underlying privileges">
                                        <i className="fas fa-unlink me-1"></i>Derived ⚠
                                      </span>
                                    )
                                  ) : (
                                    // Fixed roles (manual certification) - certification not yet implemented
                                    role.certified ? (
                                      <div>
                                        <span className="text-info">
                                          <i className="fas fa-certificate me-1"></i>Certified
                                        </span>
                                        {role.certified_by && (
                                          <div className="small text-muted mt-1">
                                            by {role.certified_by}
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-danger">
                                        <i className="fas fa-exclamation-circle me-1"></i>Pending
                                      </span>
                                    )
                                  )}
                                </td>

                                {/* Direct Server Privs Column */}
                                <td>
                                  {role.direct_server_privs ? (
                                    <a
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleShowPrivilegeDetails(role.role_name, 'server', role.direct_server_privs);
                                      }}
                                      style={{
                                        color: '#667eea',
                                        textDecoration: 'none',
                                        fontSize: '0.85rem',
                                        fontWeight: '500'
                                      }}
                                    >
                                      <i className="fas fa-server" style={{ marginRight: '5px' }}></i>
                                      {role.direct_server_privs.split(',').length} privileges
                                    </a>
                                  ) : (
                                    <span style={{ color: '#6c757d' }}>-</span>
                                  )}
                                </td>

                                {/* Direct Database Privs Column */}
                                <td>
                                  {role.direct_database_privs ? (
                                    <a
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleShowPrivilegeDetails(role.role_name, 'database', role.direct_database_privs);
                                      }}
                                      style={{
                                        color: '#667eea',
                                        textDecoration: 'none',
                                        fontSize: '0.85rem',
                                        fontWeight: '500'
                                      }}
                                    >
                                      <i className="fas fa-database" style={{ marginRight: '5px' }}></i>
                                      {role.direct_database_privs.split(',').length} privileges
                                    </a>
                                  ) : (
                                    <span style={{ color: '#6c757d' }}>-</span>
                                  )}
                                </td>

                                {/* Direct Table Privs Column */}
                                <td>
                                  {!role.is_fixed_role && role.direct_database_privs ? (
                                    <span style={{ fontSize: '0.75rem', color: '#6c757d', fontStyle: 'italic' }} title="Database-level permissions (table-specific permissions not yet collected)">
                                      <i className="fas fa-info-circle" style={{ marginRight: '4px' }}></i>
                                      DB-level only
                                    </span>
                                  ) : (
                                    <span style={{ color: '#6c757d' }}>-</span>
                                  )}
                                </td>
                              </>
                            )}
                            {selectedDatabaseType === 'POSTGRESQL' && (
                              <>
                                {/* System Role Column */}
                                <td>
                                  {role.is_system_role ? (
                                    <span className="text-info">
                                      <i className="fas fa-cog me-1"></i>System
                                    </span>
                                  ) : (
                                    <span className="text-success">
                                      <i className="fas fa-user-cog me-1"></i>Custom
                                    </span>
                                  )}
                                </td>

                                {/* Database Name Column */}
                                <td>
                                  {role.database_name ? (
                                    <small style={{ color: '#1a1a2e', fontWeight: '500' }}>
                                      {role.database_name}
                                    </small>
                                  ) : (
                                    <span style={{ color: '#6c757d' }}>-</span>
                                  )}
                                </td>

                                {/* Direct Global Privs Column */}
                                <td>
                                  {role.direct_global_privs ? (
                                    <a
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleShowPrivilegeDetails(role.role_name, 'global', role.direct_global_privs);
                                      }}
                                      style={{
                                        color: '#667eea',
                                        textDecoration: 'none',
                                        fontSize: '0.85rem',
                                        fontWeight: '500'
                                      }}
                                    >
                                      <i className="fas fa-key" style={{ marginRight: '5px' }}></i>
                                      {role.direct_global_privs.split(',').length} privileges
                                    </a>
                                  ) : (
                                    <span style={{ color: '#6c757d' }}>-</span>
                                  )}
                                </td>

                                {/* Direct Table Privs Column */}
                                <td>
                                  {role.direct_table_privs ? (
                                    <a
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleShowPrivilegeDetails(role.role_name, 'table', role.direct_table_privs);
                                      }}
                                      style={{
                                        color: '#667eea',
                                        textDecoration: 'none',
                                        fontSize: '0.85rem',
                                        fontWeight: '500'
                                      }}
                                    >
                                      <i className="fas fa-table" style={{ marginRight: '5px' }}></i>
                                      {role.direct_table_privs.split(',').length} privileges
                                    </a>
                                  ) : (
                                    <span style={{ color: '#6c757d' }}>-</span>
                                  )}
                                </td>

                                {/* Granted Roles Column */}
                                <td>
                                  {role.granted_roles ? (
                                    <a
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleShowPrivilegeDetails(role.role_name, 'roles', role.granted_roles);
                                      }}
                                      style={{
                                        color: '#667eea',
                                        textDecoration: 'none',
                                        fontSize: '0.85rem',
                                        fontWeight: '500'
                                      }}
                                    >
                                      <i className="fas fa-users-cog" style={{ marginRight: '5px' }}></i>
                                      {role.granted_roles.split(',').length} roles
                                    </a>
                                  ) : (
                                    <span style={{ color: '#6c757d' }}>-</span>
                                  )}
                                </td>
                              </>
                            )}
                            <td>
                              {role.categorized_date ? (
                                <small style={{ color: '#6c757d' }}>
                                  {new Date(role.categorized_date).toLocaleDateString()}
                                </small>
                              ) : (
                                <span style={{ color: '#6c757d' }}>-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                    <i className="fas fa-inbox" style={{ fontSize: '3rem', marginBottom: '15px', opacity: 0.3 }}></i>
                    <div style={{ fontSize: '1.1rem' }}>No roles found</div>
                    <div style={{ fontSize: '0.9rem', marginTop: '5px' }}>
                      Try adjusting your filters or run a refresh job to collect role data
                    </div>
                  </div>
                  );
                })()}
              </>
            )}
          </div>
        )}
        {/* No database type selected */}
        {!selectedDatabaseType && databaseTypes.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px', color: '#6c757d' }}>
            <i className="fas fa-database" style={{ fontSize: '4rem', marginBottom: '20px', opacity: 0.3 }}></i>
            <div style={{ fontSize: '1.2rem', fontWeight: '600' }}>No Database Types Available</div>
            <div style={{ fontSize: '1rem', marginTop: '10px' }}>
              Please ensure privilege reference tables are created for database types.
            </div>
          </div>
        )}

        {/* Edit Privilege Level Modal */}
        {showEditModal && editingPrivilege && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              width: '90%',
              maxWidth: '600px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
            }}>
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1a1a2e', marginBottom: '10px' }}>
                  <i className="fas fa-edit" style={{ marginRight: '10px', color: '#667eea' }}></i>
                  Edit Privilege Level
                </h3>
                <p style={{ color: '#6c757d', fontSize: '0.9rem', marginBottom: '5px' }}>
                  <strong>Privilege:</strong> {editingPrivilege.privilege_name}
                </p>
                <p style={{ color: '#6c757d', fontSize: '0.9rem', marginBottom: '5px' }}>
                  <strong>Type:</strong> {editingPrivilege.privilege_type}
                </p>
                <p style={{ color: '#6c757d', fontSize: '0.9rem' }}>
                  <strong>Current Level:</strong> <PrivilegeLevelBadge levelName={editingPrivilege.privilege_level_name} />
                </p>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.9rem', fontWeight: '600' }}>
                  New Privilege Level
                </label>
                <select
                  className="form-select"
                  value={editForm.privilege_level}
                  onChange={(e) => handleEditLevelChange(e.target.value)}
                  style={{ fontSize: '1rem', padding: '10px' }}
                >
                  {privilegeLevels.map(level => (
                    <option key={level.value} value={level.value}>
                      {level.name} (Level {level.value})
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.9rem', fontWeight: '600' }}>
                  Notes (Optional)
                </label>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder="Add notes about why this manual categorization was needed..."
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  style={{ fontSize: '0.9rem' }}
                />
                <small style={{ color: '#6c757d', fontSize: '0.8rem' }}>
                  Manual categorizations will be marked as certified and will not be overwritten by auto-categorization.
                </small>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  className="btn btn-outline-secondary"
                  onClick={handleCloseEditModal}
                  style={{ fontSize: '0.9rem', padding: '8px 20px' }}
                >
                  <i className="fas fa-times" style={{ marginRight: '8px' }}></i>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSavePrivilegeLevel}
                  style={{ fontSize: '0.9rem', padding: '8px 20px' }}
                >
                  <i className="fas fa-save" style={{ marginRight: '8px' }}></i>
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Role Level Modal */}
        {showEditRoleLevelModal && editingRole && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              width: '90%',
              maxWidth: '600px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
            }}>
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1a1a2e', marginBottom: '10px' }}>
                  <i className="fas fa-edit" style={{ marginRight: '10px', color: '#667eea' }}></i>
                  Edit Role Privilege Level
                </h3>
                <p style={{ color: '#6c757d', fontSize: '0.9rem', marginBottom: '5px' }}>
                  <strong>Role:</strong> {editingRole.name}
                </p>
                {!editingRole.is_bulk && (
                  <>
                    <p style={{ color: '#6c757d', fontSize: '0.9rem', marginBottom: '5px' }}>
                      <strong>Type:</strong> {editingRole.is_mssql_maintained ? 'Fixed' : 'User-Defined'} {editingRole.is_server_role ? 'Server' : 'Database'} Role
                    </p>
                    <p style={{ color: '#6c757d', fontSize: '0.9rem' }}>
                      <strong>Current Level:</strong> <PrivilegeLevelBadge levelName={editingRole.privilege_level_name} />
                    </p>
                  </>
                )}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.9rem', fontWeight: '600' }}>
                  New Privilege Level
                </label>
                <select
                  className="form-select"
                  value={editRoleForm.privilege_level}
                  onChange={(e) => handleEditRoleLevelChange(e.target.value)}
                  style={{ fontSize: '1rem', padding: '10px' }}
                >
                  <option value="">Select Level...</option>
                  {privilegeLevels.map(level => (
                    <option key={level.value} value={level.value}>
                      {level.name} (Level {level.value})
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.9rem', fontWeight: '600' }}>
                  Notes (Optional)
                </label>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder="Add notes about why this manual categorization was needed..."
                  value={editRoleForm.notes}
                  onChange={(e) => setEditRoleForm({ ...editRoleForm, notes: e.target.value })}
                  style={{ fontSize: '0.9rem' }}
                />
                <small style={{ color: '#6c757d', fontSize: '0.8rem' }}>
                  Manual categorizations for fixed roles help improve accuracy. Only fixed roles can be manually categorized.
                </small>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="certifyAfterUpdate"
                    checked={editRoleForm.certify_after_update}
                    onChange={(e) => setEditRoleForm({ ...editRoleForm, certify_after_update: e.target.checked })}
                  />
                  <label className="form-check-label" htmlFor="certifyAfterUpdate" style={{ fontSize: '0.9rem' }}>
                    <i className="fas fa-certificate" style={{ marginRight: '5px', color: '#0ea5e9' }}></i>
                    Certify after update
                  </label>
                </div>
                <small style={{ color: '#6c757d', fontSize: '0.8rem', marginLeft: '24px', display: 'block', marginTop: '4px' }}>
                  Mark this role as certified after updating the privilege level
                </small>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  className="btn btn-outline-secondary"
                  onClick={handleCloseEditRoleLevelModal}
                  style={{ fontSize: '0.9rem', padding: '8px 20px' }}
                >
                  <i className="fas fa-times" style={{ marginRight: '8px' }}></i>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveRoleLevel}
                  style={{ fontSize: '0.9rem', padding: '8px 20px' }}
                >
                  <i className="fas fa-save" style={{ marginRight: '8px' }}></i>
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Privilege Detail Modal (for Role Privileges) */}
        {showPrivilegeDetailModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
            onClick={() => setShowPrivilegeDetailModal(false)}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '30px',
                width: '90%',
                maxWidth: '700px',
                maxHeight: '80vh',
                overflow: 'auto',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1a1a2e', marginBottom: '10px' }}>
                  <i className={
                    privilegeDetailData.type === 'system' ? 'fas fa-list-ul' :
                    privilegeDetailData.type === 'table' ? 'fas fa-table' :
                    'fas fa-users-cog'
                  } style={{ marginRight: '10px', color: '#667eea' }}></i>
                  {privilegeDetailData.title}
                </h3>
                <p style={{ color: '#6c757d', fontSize: '0.95rem' }}>
                  <strong>Role:</strong> {privilegeDetailData.roleName}
                </p>
                <p style={{ color: '#6c757d', fontSize: '0.9rem', marginTop: '5px' }}>
                  <strong>Total Count:</strong> {privilegeDetailData.items.length}
                </p>
                {selectedDatabaseType === 'MSSQL' && privilegeDetailData.type === 'database' && (
                  <div style={{
                    marginTop: '10px',
                    padding: '8px 12px',
                    background: '#fff3e0',
                    borderLeft: '4px solid #f57c00',
                    borderRadius: '4px',
                    fontSize: '0.85rem',
                    color: '#e65100'
                  }}>
                    <i className="fas fa-info-circle" style={{ marginRight: '6px' }}></i>
                    <strong>Note:</strong> These are database-level permissions. Specific database and table names are not yet collected.
                  </div>
                )}
              </div>

              <div style={{
                background: '#f8f9fa',
                borderRadius: '8px',
                padding: '15px',
                maxHeight: '400px',
                overflowY: 'auto'
              }}>
                {privilegeDetailData.items.length > 0 ? (
                  privilegeDetailData.type === 'table' ? (
                    /* Special rendering for table privileges with schema.table info */
                    <ul style={{
                      listStyle: 'none',
                      padding: 0,
                      margin: 0
                    }}>
                      {privilegeDetailData.items.map((item, index) => {
                        // Parse "PRIVILEGE on OWNER.TABLE_NAME" format
                        const match = item.match(/^(.+?)\s+on\s+(.+?)\.(.+)$/);
                        if (match) {
                          const [, privilege, schema, table] = match;
                          return (
                            <li key={index} style={{
                              padding: '8px 12px',
                              borderBottom: index < privilegeDetailData.items.length - 1 ? '1px solid #dee2e6' : 'none',
                              fontSize: '0.9rem'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                <i className="fas fa-check-circle" style={{ color: '#28a745', flexShrink: 0 }}></i>
                                <span style={{
                                  fontWeight: '600',
                                  color: '#667eea',
                                  fontFamily: 'monospace',
                                  fontSize: '0.9rem'
                                }}>
                                  {privilege}
                                </span>
                                <span style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                                  on
                                </span>
                                <span style={{
                                  background: '#e7f3ff',
                                  color: '#0066cc',
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  fontSize: '0.8rem',
                                  fontFamily: 'monospace'
                                }}>
                                  {schema}
                                </span>
                                <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>.</span>
                                <span style={{
                                  background: '#fff3e0',
                                  color: '#e65100',
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  fontSize: '0.8rem',
                                  fontFamily: 'monospace',
                                  fontWeight: '500'
                                }}>
                                  {table}
                                </span>
                              </div>
                            </li>
                          );
                        } else {
                          // Fallback for old format or unparseable entries
                          return (
                            <li key={index} style={{
                              padding: '8px 12px',
                              borderBottom: index < privilegeDetailData.items.length - 1 ? '1px solid #dee2e6' : 'none',
                              fontSize: '0.9rem',
                              color: '#1a1a2e',
                              fontFamily: 'monospace'
                            }}>
                              <i className="fas fa-check-circle" style={{ color: '#28a745', marginRight: '8px' }}></i>
                              {item}
                            </li>
                          );
                        }
                      })}
                    </ul>
                  ) : privilegeDetailData.type === 'roles' ? (
                    /* Enhanced rendering for granted roles with privilege levels */
                    <ul style={{
                      listStyle: 'none',
                      padding: 0,
                      margin: 0
                    }}>
                      {privilegeDetailData.items.map((roleName, index) => {
                        // Look up the role in the roles state to get its privilege level
                        const roleInfo = roles.find(r => r.role_name === roleName);
                        const privilegeLevel = roleInfo?.privilege_level_name || 'UNKNOWN';

                        // Color mapping for privilege levels
                        const levelColors = {
                          'SUPER_ADMIN': { bg: '#ffe5e5', text: '#d32f2f', icon: 'fa-crown' },
                          'ADMIN': { bg: '#fff3e0', text: '#e65100', icon: 'fa-user-shield' },
                          'WRITE': { bg: '#fff9e6', text: '#f57c00', icon: 'fa-edit' },
                          'READ': { bg: '#e8f5e9', text: '#2e7d32', icon: 'fa-eye' },
                          'UNKNOWN': { bg: '#f5f5f5', text: '#757575', icon: 'fa-question-circle' }
                        };

                        const colors = levelColors[privilegeLevel] || levelColors['UNKNOWN'];

                        return (
                          <li key={index} style={{
                            padding: '10px 12px',
                            borderBottom: index < privilegeDetailData.items.length - 1 ? '1px solid #dee2e6' : 'none',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            <i className="fas fa-check-circle" style={{ color: '#28a745', flexShrink: 0 }}></i>

                            {/* Clickable role name */}
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                handleShowRoleDetails(
                                  roleName,
                                  privilegeDetailData.parentDatabaseName,
                                  privilegeDetailData.parentDbid
                                );
                              }}
                              style={{
                                color: '#0066cc',
                                textDecoration: 'none',
                                fontWeight: '600',
                                fontFamily: 'monospace',
                                fontSize: '0.9rem',
                                cursor: 'pointer',
                                flex: '0 0 auto'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                            >
                              <i className="fas fa-link" style={{ marginRight: '6px', fontSize: '0.75rem' }}></i>
                              {roleName}
                            </a>

                            {/* Privilege level badge */}
                            <span style={{
                              background: colors.bg,
                              color: colors.text,
                              padding: '4px 10px',
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              flex: '0 0 auto'
                            }}>
                              <i className={`fas ${colors.icon}`} style={{ fontSize: '0.7rem' }}></i>
                              {privilegeLevel}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    /* Standard rendering for system privileges */
                    <ul style={{
                      listStyle: 'none',
                      padding: 0,
                      margin: 0
                    }}>
                      {privilegeDetailData.items.map((item, index) => (
                        <li key={index} style={{
                          padding: '8px 12px',
                          borderBottom: index < privilegeDetailData.items.length - 1 ? '1px solid #dee2e6' : 'none',
                          fontSize: '0.9rem',
                          color: '#1a1a2e',
                          fontFamily: 'monospace'
                        }}>
                          <i className="fas fa-check-circle" style={{ color: '#28a745', marginRight: '8px' }}></i>
                          {item}
                        </li>
                      ))}
                    </ul>
                  )
                ) : (
                  <p style={{ textAlign: 'center', color: '#6c757d', margin: 0 }}>
                    No items found
                  </p>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowPrivilegeDetailModal(false)}
                  style={{ fontSize: '0.9rem', padding: '8px 20px' }}
                >
                  <i className="fas fa-times" style={{ marginRight: '8px' }}></i>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Role Detail Modal - Drill-down view for role information */}
        {showRoleDetailModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000 // Higher than privilege detail modal
          }}
          onClick={() => setShowRoleDetailModal(false)}
          >
            <div style={{
              background: '#fff',
              borderRadius: '12px',
              maxWidth: '900px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'hidden',
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div style={{
                padding: '20px 25px',
                borderBottom: '2px solid #e0e0e0',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff'
              }}>
                <h3 style={{ margin: 0, fontSize: '1.3rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <i className="fas fa-user-shield"></i>
                  Role Details
                </h3>
              </div>

              {/* Modal Body */}
              <div style={{
                padding: '25px',
                overflowY: 'auto',
                flex: 1
              }}>
                {roleDetailLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#667eea' }}></i>
                    <p style={{ marginTop: '15px', color: '#6c757d' }}>Loading role details...</p>
                  </div>
                ) : roleDetailData ? (
                  <div>
                    {/* Role Header Information */}
                    <div style={{
                      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                      padding: '20px',
                      borderRadius: '8px',
                      marginBottom: '25px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
                        <h4 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '700', color: '#1a1a2e' }}>
                          {roleDetailData.role_name}
                        </h4>
                        <span style={{
                          background: roleDetailData.privilege_level_name === 'SUPER_ADMIN' ? '#ffe5e5' :
                                    roleDetailData.privilege_level_name === 'ADMIN' ? '#fff3e0' :
                                    roleDetailData.privilege_level_name === 'WRITE' ? '#fff9e6' : '#e8f5e9',
                          color: roleDetailData.privilege_level_name === 'SUPER_ADMIN' ? '#d32f2f' :
                                 roleDetailData.privilege_level_name === 'ADMIN' ? '#e65100' :
                                 roleDetailData.privilege_level_name === 'WRITE' ? '#f57c00' : '#2e7d32',
                          padding: '6px 12px',
                          borderRadius: '12px',
                          fontSize: '0.85rem',
                          fontWeight: '600'
                        }}>
                          {roleDetailData.privilege_level_name}
                        </span>
                        {roleDetailData.is_oracle_maintained && (
                          <span style={{
                            background: '#e3f2fd',
                            color: '#1976d2',
                            padding: '6px 12px',
                            borderRadius: '12px',
                            fontSize: '0.85rem',
                            fontWeight: '600'
                          }}>
                            <i className="fas fa-shield-alt" style={{ marginRight: '6px' }}></i>
                            Oracle Maintained
                          </span>
                        )}
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                        {roleDetailData.database_name && (
                          <div>
                            <span style={{ fontSize: '0.8rem', color: '#6c757d', display: 'block' }}>Database</span>
                            <span style={{ fontSize: '0.95rem', fontWeight: '600', color: '#1a1a2e' }}>
                              {roleDetailData.database_name}
                            </span>
                          </div>
                        )}
                        {roleDetailData.categorized_at && (
                          <div>
                            <span style={{ fontSize: '0.8rem', color: '#6c757d', display: 'block' }}>Categorized</span>
                            <span style={{ fontSize: '0.95rem', fontWeight: '600', color: '#1a1a2e' }}>
                              {new Date(roleDetailData.categorized_at).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Privilege Sections */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      {/* Database-Specific Privilege Sections */}
                      {(() => {
                        // Helper function to get section configuration based on database type
                        const getSectionConfig = () => {
                          if (selectedDatabaseType === 'CASSANDRA') {
                            // For Cassandra, group by resource scope
                            const privileges = roleDetailData.direct_system_privs ?
                              roleDetailData.direct_system_privs.split(',').map(p => p.trim()) : [];

                            // Group privileges by scope
                            const clusterPrivs = privileges.filter(p => p.includes('on <all ') || p.includes('on ALL'));
                            const keyspacePrivs = privileges.filter(p => p.match(/on <keyspace |on [a-z_]+\//) && !p.includes('<all'));
                            const tablePrivs = privileges.filter(p => p.match(/on <table |\/[a-z_]+>/));
                            const rolesPrivs = privileges.filter(p => p.includes('on <roles>') || p.includes('AUTHORIZE'));
                            const functionsPrivs = privileges.filter(p => p.includes('on <functions>') || p.includes('EXECUTE'));
                            const mbeanPrivs = privileges.filter(p => p.includes('on <mbean>') || p.includes('on mbean'));

                            return [
                              {
                                title: 'Cluster Level Permissions',
                                icon: 'fa-server',
                                color: '#667eea',
                                items: clusterPrivs,
                                emptyMsg: 'No cluster level permissions'
                              },
                              {
                                title: 'Keyspace Level Permissions',
                                icon: 'fa-database',
                                color: '#764ba2',
                                items: keyspacePrivs,
                                emptyMsg: 'No keyspace level permissions'
                              },
                              {
                                title: 'Table Level Permissions',
                                icon: 'fa-table',
                                color: '#f09819',
                                items: tablePrivs,
                                emptyMsg: 'No table level permissions'
                              },
                              {
                                title: 'Role Management Permissions',
                                icon: 'fa-users-cog',
                                color: '#28a745',
                                items: rolesPrivs,
                                emptyMsg: 'No role management permissions'
                              },
                              {
                                title: 'Function Permissions',
                                icon: 'fa-code',
                                color: '#17a2b8',
                                items: functionsPrivs,
                                emptyMsg: 'No function permissions'
                              },
                              {
                                title: 'MBean Permissions',
                                icon: 'fa-cogs',
                                color: '#6c757d',
                                items: mbeanPrivs,
                                emptyMsg: 'No MBean permissions'
                              }
                            ].filter(section => section.items.length > 0); // Only show non-empty sections
                          } else if (selectedDatabaseType === 'MYSQL') {
                            return [
                              {
                                title: 'Global Privileges',
                                icon: 'fa-globe',
                                color: '#667eea',
                                items: roleDetailData.direct_system_privs ? roleDetailData.direct_system_privs.split(',').map(p => p.trim()) : [],
                                emptyMsg: 'No global privileges'
                              },
                              {
                                title: 'Database Privileges',
                                icon: 'fa-database',
                                color: '#764ba2',
                                items: roleDetailData.direct_table_privs ? roleDetailData.direct_table_privs.split(',').map(p => p.trim()) : [],
                                emptyMsg: 'No database privileges',
                                parseFormat: 'oracle' // Use Oracle parsing format
                              }
                            ];
                          } else if (selectedDatabaseType === 'MSSQL') {
                            return [
                              {
                                title: 'Server Level Privileges',
                                icon: 'fa-server',
                                color: '#667eea',
                                items: roleDetailData.direct_system_privs ? roleDetailData.direct_system_privs.split(',').map(p => p.trim()) : [],
                                emptyMsg: 'No server level privileges'
                              },
                              {
                                title: 'Database Level Privileges',
                                icon: 'fa-database',
                                color: '#764ba2',
                                items: roleDetailData.direct_table_privs ? roleDetailData.direct_table_privs.split(',').map(p => p.trim()) : [],
                                emptyMsg: 'No database level privileges',
                                parseFormat: 'oracle'
                              }
                            ];
                          } else {
                            // Default to Oracle/generic format
                            return [
                              {
                                title: 'Direct System Privileges',
                                icon: 'fa-cog',
                                color: '#667eea',
                                items: roleDetailData.direct_system_privs ? roleDetailData.direct_system_privs.split(',').map(p => p.trim()) : [],
                                emptyMsg: 'No direct system privileges'
                              },
                              {
                                title: 'Direct Table Privileges',
                                icon: 'fa-table',
                                color: '#764ba2',
                                items: roleDetailData.direct_table_privs ? roleDetailData.direct_table_privs.split(',').map(p => p.trim()) : [],
                                emptyMsg: 'No direct table privileges',
                                parseFormat: 'oracle'
                              }
                            ];
                          }
                        };

                        const sections = getSectionConfig();

                        return sections.map((section, sectionIdx) => (
                          <div key={sectionIdx} style={{
                            border: '1px solid #dee2e6',
                            borderRadius: '8px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              background: '#f8f9fa',
                              padding: '12px 15px',
                              borderBottom: '1px solid #dee2e6',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px'
                            }}>
                              <i className={`fas ${section.icon}`} style={{ color: section.color }}></i>
                              <h5 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: '#1a1a2e' }}>
                                {section.title}
                              </h5>
                              <span style={{
                                marginLeft: 'auto',
                                background: section.color,
                                color: '#fff',
                                padding: '2px 8px',
                                borderRadius: '10px',
                                fontSize: '0.75rem',
                                fontWeight: '600'
                              }}>
                                {section.items.length}
                              </span>
                            </div>
                            <div style={{ padding: '15px', maxHeight: '200px', overflowY: 'auto' }}>
                              {section.items.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: section.parseFormat === 'oracle' ? 'column' : 'wrap', flexWrap: section.parseFormat === 'oracle' ? 'nowrap' : 'wrap', gap: '8px' }}>
                                  {section.items.map((priv, idx) => {
                                    // Parse Oracle/MySQL format: "PRIVILEGE on SCHEMA.TABLE"
                                    if (section.parseFormat === 'oracle') {
                                      const match = priv.match(/^(.+?)\s+on\s+(.+?)\.(.+)$/);
                                      if (match) {
                                        const [, privilege, schema, table] = match;
                                        return (
                                          <div key={idx} style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            flexWrap: 'wrap'
                                          }}>
                                            <span style={{
                                              color: '#667eea',
                                              fontWeight: '600',
                                              fontSize: '0.85rem'
                                            }}>
                                              {privilege}
                                            </span>
                                            <span style={{ color: '#6c757d', fontSize: '0.8rem' }}>on</span>
                                            <span style={{
                                              background: '#e7f3ff',
                                              color: '#0066cc',
                                              padding: '2px 6px',
                                              borderRadius: '4px',
                                              fontSize: '0.75rem',
                                              fontFamily: 'monospace'
                                            }}>
                                              {schema}
                                            </span>
                                            <span style={{ color: '#6c757d' }}>.</span>
                                            <span style={{
                                              background: '#fff3e0',
                                              color: '#e65100',
                                              padding: '2px 6px',
                                              borderRadius: '4px',
                                              fontSize: '0.75rem',
                                              fontFamily: 'monospace'
                                            }}>
                                              {table}
                                            </span>
                                          </div>
                                        );
                                      }
                                    }

                                    // Default format: just show the privilege
                                    return (
                                      <span key={idx} style={{
                                        background: '#e7f3ff',
                                        color: '#0066cc',
                                        padding: '4px 10px',
                                        borderRadius: '6px',
                                        fontSize: '0.8rem',
                                        fontFamily: 'monospace'
                                      }}>
                                        {priv}
                                      </span>
                                    );
                                  })}
                                </div>
                              ) : (
                                <p style={{ margin: 0, color: '#6c757d', fontSize: '0.9rem' }}>{section.emptyMsg}</p>
                              )}
                            </div>
                          </div>
                        ));
                      })()}

                      {/* Assigned to Users */}
                      <div style={{
                        border: '1px solid #dee2e6',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        marginBottom: '15px'
                      }}>
                        <div style={{
                          background: '#f8f9fa',
                          padding: '12px 15px',
                          borderBottom: '1px solid #dee2e6',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px'
                        }}>
                          <i className="fas fa-users" style={{ color: '#17a2b8' }}></i>
                          <h5 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: '#1a1a2e' }}>
                            Assigned to Users
                          </h5>
                          <span style={{
                            marginLeft: 'auto',
                            background: '#17a2b8',
                            color: '#fff',
                            padding: '2px 8px',
                            borderRadius: '10px',
                            fontSize: '0.75rem',
                            fontWeight: '600'
                          }}>
                            {roleDetailData.assigned_to_users ? roleDetailData.assigned_to_users.split(',').length : 0}
                          </span>
                        </div>
                        <div style={{ padding: '15px', maxHeight: '200px', overflowY: 'auto' }}>
                          {roleDetailData.assigned_to_users ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                              {roleDetailData.assigned_to_users.split(',').map((userName, idx) => (
                                <span
                                  key={idx}
                                  style={{
                                    background: '#d1ecf1',
                                    color: '#0c5460',
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    fontSize: '0.8rem',
                                    fontFamily: 'monospace',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    border: '1px solid #bee5eb'
                                  }}
                                >
                                  <i className="fas fa-user" style={{ fontSize: '0.7rem' }}></i>
                                  {userName.trim()}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p style={{ margin: 0, color: '#6c757d', fontSize: '0.9rem' }}>
                              No users assigned
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Nested Roles */}
                      <div style={{
                        border: '1px solid #dee2e6',
                        borderRadius: '8px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          background: '#f8f9fa',
                          padding: '12px 15px',
                          borderBottom: '1px solid #dee2e6',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px'
                        }}>
                          <i className="fas fa-users-cog" style={{ color: '#28a745' }}></i>
                          <h5 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: '#1a1a2e' }}>
                            Nested Roles
                          </h5>
                          <span style={{
                            marginLeft: 'auto',
                            background: '#28a745',
                            color: '#fff',
                            padding: '2px 8px',
                            borderRadius: '10px',
                            fontSize: '0.75rem',
                            fontWeight: '600'
                          }}>
                            {roleDetailData.granted_roles ? roleDetailData.granted_roles.split(',').length : 0}
                          </span>
                        </div>
                        <div style={{ padding: '15px', maxHeight: '200px', overflowY: 'auto' }}>
                          {roleDetailData.granted_roles ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                              {roleDetailData.granted_roles.split(',').map((grantedRole, idx) => {
                                const grantedRoleInfo = roles.find(r => r.role_name === grantedRole.trim());
                                const grantedRoleLevel = grantedRoleInfo?.privilege_level_name || 'UNKNOWN';

                                return (
                                  <a
                                    key={idx}
                                    href="#"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleShowRoleDetails(
                                        grantedRole.trim(),
                                        roleDetailData.database_name,
                                        roleDetailData.dbid
                                      );
                                    }}
                                    style={{
                                      background: '#e8f5e9',
                                      color: '#2e7d32',
                                      padding: '6px 12px',
                                      borderRadius: '6px',
                                      fontSize: '0.8rem',
                                      fontFamily: 'monospace',
                                      textDecoration: 'none',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      transition: 'all 0.2s',
                                      border: '1px solid #c8e6c9'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = '#c8e6c9';
                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = '#e8f5e9';
                                      e.currentTarget.style.transform = 'translateY(0)';
                                    }}
                                  >
                                    <i className="fas fa-link" style={{ fontSize: '0.7rem' }}></i>
                                    {grantedRole.trim()}
                                    <span style={{
                                      fontSize: '0.7rem',
                                      opacity: 0.8,
                                      marginLeft: '4px'
                                    }}>
                                      ({grantedRoleLevel})
                                    </span>
                                  </a>
                                );
                              })}
                            </div>
                          ) : (
                            <p style={{ margin: 0, color: '#6c757d', fontSize: '0.9rem' }}>
                              No nested roles
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <i className="fas fa-exclamation-circle" style={{ fontSize: '2rem', color: '#dc3545' }}></i>
                    <p style={{ marginTop: '15px', color: '#6c757d' }}>Failed to load role details</p>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '15px 25px', borderTop: '1px solid #dee2e6' }}>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowRoleDetailModal(false)}
                  style={{ fontSize: '0.9rem', padding: '8px 20px' }}
                >
                  <i className="fas fa-times" style={{ marginRight: '8px' }}></i>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Error modal — used for bulk-certification failures (e.g. UNASSIGNED
            privileges blocking the batch) so the full list is readable rather
            than truncated in a blocking alert(). */}
        <WideModal
          show={!!errorModal}
          onClose={() => setErrorModal(null)}
          title={errorModal?.title || 'Error'}
          headerIcon="fa-exclamation-triangle"
          headerClass="bg-danger text-white"
          maxWidth="700px"
          footer={
            <button className="btn btn-secondary" onClick={() => setErrorModal(null)}>
              Close
            </button>
          }
        >
          <p style={{ marginBottom: 0, fontSize: '0.95rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {errorModal?.message}
          </p>
        </WideModal>

        {/* Bulk Apply Suggested Levels — review the per-row documentation
            suggestions for the selected UNASSIGNED items, optionally certify,
            and apply in one action. Each row keeps its OWN suggested level. */}
        <WideModal
          show={showApplySuggestedModal}
          onClose={() => !applySuggestedBusy && setShowApplySuggestedModal(false)}
          title="Apply Suggested Privilege Levels"
          headerIcon="fa-lightbulb"
          headerClass="bg-info text-white"
          footer={
            <>
              <button
                className="btn btn-secondary"
                onClick={() => setShowApplySuggestedModal(false)}
                disabled={applySuggestedBusy}
              >
                Cancel
              </button>
              <button
                className="btn btn-info text-white"
                onClick={handleApplySuggestedLevels}
                disabled={applySuggestedBusy || applySuggestedRows.length === 0}
              >
                {applySuggestedBusy ? (
                  <><i className="fas fa-spinner fa-spin me-2"></i>Applying…</>
                ) : (
                  <><i className="fas fa-check me-2"></i>
                    Apply to {applySuggestedRows.length} item(s)
                    {applySuggestedCertify ? ' & Certify' : ''}</>
                )}
              </button>
            </>
          }
        >
          <div style={{ marginBottom: '12px', fontSize: '0.9rem', color: '#555' }}>
            These items are UNASSIGNED with a documentation-derived suggestion
            (already clamped to the privilege matrix's enabled tiers). Applying
            sets each row to <strong>its own</strong> suggested level. Uncheck any
            you don't want to include.
          </div>
          <div style={{
            marginBottom: '14px', padding: '10px 12px', background: '#e7f3ff',
            border: '1px solid #b3d9ff', borderRadius: '6px'
          }}>
            <label style={{ fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', marginBottom: 0 }}>
              <input
                type="checkbox"
                checked={applySuggestedCertify}
                onChange={(e) => setApplySuggestedCertify(e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Also certify these items now (assign level &amp; certify in one step)
            </label>
          </div>
          <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
            <table className="table table-sm table-hover mb-0">
              <thead className="table-dark" style={{ position: 'sticky', top: 0 }}>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={suggestableSelected.length > 0
                        && applySuggestedRows.length === suggestableSelected.length}
                      onChange={(e) => setApplySuggestedRows(
                        e.target.checked ? suggestableSelected.map(p => p.id) : []
                      )}
                    />
                  </th>
                  <th>Privilege / Role</th>
                  <th>Type</th>
                  <th>Current</th>
                  <th>Suggested →</th>
                </tr>
              </thead>
              <tbody>
                {suggestableSelected.map(p => (
                  <tr key={p.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={applySuggestedRows.includes(p.id)}
                        onChange={(e) => setApplySuggestedRows(prev =>
                          e.target.checked ? [...prev, p.id] : prev.filter(id => id !== p.id)
                        )}
                      />
                    </td>
                    <td style={{ fontWeight: 600 }}>{p.privilege_name}</td>
                    <td>{p.privilege_type}</td>
                    <td>
                      <span className="text-danger">
                        <i className="fas fa-tag me-1"></i>UNASSIGNED
                      </span>
                    </td>
                    <td>
                      <PrivilegeLevelBadge levelName={p.suggested_level_name} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </WideModal>

        {/* Bulk Apply Suggested Levels — ROLES (predefined roles). Mirrors the
            privileges modal; each role keeps its OWN suggested level. */}
        <WideModal
          show={showApplySuggestedRoleModal}
          onClose={() => !applySuggestedRoleBusy && setShowApplySuggestedRoleModal(false)}
          title="Apply Suggested Levels — Predefined Roles"
          headerIcon="fa-lightbulb"
          headerClass="bg-info text-white"
          footer={
            <>
              <button
                className="btn btn-secondary"
                onClick={() => setShowApplySuggestedRoleModal(false)}
                disabled={applySuggestedRoleBusy}
              >
                Cancel
              </button>
              <button
                className="btn btn-info text-white"
                onClick={handleApplySuggestedRolesConfirm}
                disabled={applySuggestedRoleBusy || applySuggestedRoleRows.length === 0}
              >
                {applySuggestedRoleBusy ? (
                  <><i className="fas fa-spinner fa-spin me-2"></i>Applying…</>
                ) : (
                  <><i className="fas fa-check me-2"></i>
                    Apply to {applySuggestedRoleRows.length} role(s)
                    {applySuggestedRoleCertify ? ' & Certify' : ''}</>
                )}
              </button>
            </>
          }
        >
          <div style={{ marginBottom: '12px', fontSize: '0.9rem', color: '#555' }}>
            These predefined roles are UNASSIGNED with a documentation-derived
            suggestion (clamped to the privilege matrix). Applying sets each role
            to <strong>its own</strong> suggested level. Uncheck any to exclude.
          </div>
          <div style={{
            marginBottom: '14px', padding: '10px 12px', background: '#e7f3ff',
            border: '1px solid #b3d9ff', borderRadius: '6px'
          }}>
            <label style={{ fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', marginBottom: 0 }}>
              <input
                type="checkbox"
                checked={applySuggestedRoleCertify}
                onChange={(e) => setApplySuggestedRoleCertify(e.target.checked)}
                style={{ marginRight: '8px' }}
                disabled={selectedDatabaseType !== 'MSSQL'}
              />
              Also certify these roles now
              {selectedDatabaseType !== 'MSSQL' && (
                <span style={{ fontWeight: 400, color: '#666' }}>
                  {' '}(certification applies to MSSQL roles only)
                </span>
              )}
            </label>
          </div>
          <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
            <table className="table table-sm table-hover mb-0">
              <thead className="table-dark" style={{ position: 'sticky', top: 0 }}>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={suggestableRolesForModal.length > 0
                        && applySuggestedRoleRows.length === suggestableRolesForModal.length}
                      onChange={(e) => setApplySuggestedRoleRows(
                        e.target.checked ? suggestableRolesForModal.map(r => r.id) : []
                      )}
                    />
                  </th>
                  <th>Role</th>
                  <th>Current</th>
                  <th>Suggested →</th>
                </tr>
              </thead>
              <tbody>
                {suggestableRolesForModal.map(r => (
                  <tr key={r.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={applySuggestedRoleRows.includes(r.id)}
                        onChange={(e) => setApplySuggestedRoleRows(prev =>
                          e.target.checked ? [...prev, r.id] : prev.filter(id => id !== r.id)
                        )}
                      />
                    </td>
                    <td style={{ fontWeight: 600 }}>{r.role_name}</td>
                    <td>
                      <span className="text-danger">
                        <i className="fas fa-tag me-1"></i>UNASSIGNED
                      </span>
                    </td>
                    <td>
                      <PrivilegeLevelBadge levelName={r.suggested_level_name} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </WideModal>
      </div>
    </div>
  );
};

export default ManagePrivilegeMappings;
